import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Chip } from "primereact/chip";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ROLE_TYPE_CAREGIVER, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import Unauthorized from "../../../shared/pages/Unauthorized";
import AdminResetControl from "../../../components/general/AdminResetControl";

const LOS_ANGELES_TIME_ZONE = "America/Los_Angeles";

const EMPTY_EVENT_FORM = {
  eventId: "",
  eventType: "meeting",
  title: "",
  description: "",
  startAt: null as Date | null,
  endAt: null as Date | null,
  allDay: false,
  homeId: "",
  residentId: "",
  notificationEmailReminder: "none",
  repeatEnabled: "no",
  recurrenceFrequency: "none",
  recurrenceInterval: "1",
  recurrenceUntil: null as Date | null,
};

const CALENDAR_VIEW_OPTIONS = [
  { label: "Month", value: "month" },
  { label: "Week", value: "week" },
  { label: "Day", value: "day" },
];

const RECURRENCE_FREQUENCY_OPTIONS = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];

const RECURRING_EDIT_SCOPE_OPTIONS = [
  { label: "This event", value: "this_event" },
  { label: "Entire series", value: "entire_series" },
];

const BASE_REMINDER_OPTIONS = [
  { label: "No email reminder", value: "none" },
  { label: "15 minutes before", value: "15m" },
  { label: "1 hour before", value: "1h" },
  { label: "1 day before", value: "1d" },
];

const REPEAT_TOGGLE_OPTIONS = [
  { label: "Off", value: "no" },
  { label: "On", value: "yes" },
];

const DEFAULT_EVENT_TYPE_OPTIONS = [
  { label: "Meeting", value: "meeting" },
  { label: "Resident Appointment", value: "resident_appointment" },
  { label: "Document Expiration", value: "document_expiration" },
  { label: "General Reminder", value: "general_reminder" },
];

const START_TIME_OPTIONS = Array.from({ length: 96 }, (_, index) => {
  const hour24 = Math.floor(index / 4);
  const minute = (index % 4) * 15;
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return {
    label: `${hour12}:${String(minute).padStart(2, "0")} ${period}`,
    value: `${String(hour24).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00`,
  };
});

const addDays = (value: Date, days: number) => {
  const nextDate = new Date(value);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const startOfMonth = (value: Date) => new Date(value.getFullYear(), value.getMonth(), 1);

const startOfWeek = (value: Date) => {
  const nextDate = new Date(value);
  const weekday = nextDate.getDay();
  nextDate.setDate(nextDate.getDate() - weekday);
  nextDate.setHours(0, 0, 0, 0);
  return nextDate;
};

const getMonthGridDays = (value: Date) => {
  const firstDayOfMonth = startOfMonth(value);
  const gridStart = startOfWeek(firstDayOfMonth);
  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
};

const sharedCalendarSurfaceCardStyle: React.CSSProperties = {
  border: "1px solid #dbe7f1",
  boxShadow: "0 16px 34px rgba(15, 23, 42, 0.06)",
  borderRadius: "18px",
  background: "linear-gradient(180deg, #f9fbfd 0%, #ffffff 100%)",
};

const sharedCalendarCardHeaderStyle: React.CSSProperties = {
  fontWeight: 700,
  color: "#0f172a",
  letterSpacing: "-0.01em",
};

const sharedCalendarCardSubtleTextStyle: React.CSSProperties = {
  color: "#64748b",
  lineHeight: 1.55,
};

const getHomeAcronym = (value: any) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return "";
  }
  const words = normalized.match(/[A-Za-z0-9]+/g) || [];
  if (words.length <= 1) {
    return normalized.slice(0, 5).toUpperCase();
  }
  return words.map((word) => word.charAt(0)).join("").toUpperCase().slice(0, 8);
};

const getLosAngelesOffsetSuffix = (value: Date) => {
  const timeZonePart = new Intl.DateTimeFormat("en-US", {
    timeZone: LOS_ANGELES_TIME_ZONE,
    timeZoneName: "shortOffset",
  }).formatToParts(value).find((part) => part.type === "timeZoneName")?.value || "GMT-8";

  const normalized = timeZonePart.replace("GMT", "");
  if (!normalized || normalized === "0") {
    return "Z";
  }

  const sign = normalized.startsWith("-") ? "-" : "+";
  const raw = normalized.replace(/[+-]/g, "");
  const [hours = "0", minutes = "0"] = raw.split(":");
  return `${sign}${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
};

const SharedCalendarPage = () => {
  const { authStore, backendAPIStore } = useContext(MobXProviderContext);
  const mobile = useResponsive().isMobile;
  const toast = useRef<Toast>(null);
  const userRoleType = authStore.getUserRoleType();
  const isAdminUser = authStore.isAdmin();
  const isClinicalStaffRole = userRoleType === ROLE_TYPE_NURSE || userRoleType === ROLE_TYPE_MHP;
  const canAccessSharedCalendar = isAdminUser
    || userRoleType === ROLE_TYPE_PROVIDER
    || userRoleType === ROLE_TYPE_NURSE
    || userRoleType === ROLE_TYPE_MHP
    || userRoleType === ROLE_TYPE_CAREGIVER;
  const reminderOptions = useMemo(() => (
    isAdminUser
      ? [
        ...BASE_REMINDER_OPTIONS.slice(0, 1),
        { label: "13 minutes before", value: "13m" },
        ...BASE_REMINDER_OPTIONS.slice(1),
      ]
      : BASE_REMINDER_OPTIONS
  ), [isAdminUser]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [draggingEventId, setDraggingEventId] = useState("");
  const [hoveredCalendarDayKey, setHoveredCalendarDayKey] = useState("");
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");
  const [showCancelledUpcomingOnly, setShowCancelledUpcomingOnly] = useState(false);
  const [filterDialogVisible, setFilterDialogVisible] = useState(false);
  const [draftScopeValue, setDraftScopeValue] = useState("all");
  const [draftEventType, setDraftEventType] = useState("all");
  const [draftSearchValue, setDraftSearchValue] = useState("");
  const [draftActiveOnly, setDraftActiveOnly] = useState(true);
  const [selectedEventType, setSelectedEventType] = useState("all");
  const [selectedHomeId, setSelectedHomeId] = useState("all");
  const [selectedResidentFilterId, setSelectedResidentFilterId] = useState("all");
  const [selectedCalendarView, setSelectedCalendarView] = useState("month");
  const [searchValue, setSearchValue] = useState("");
  const [eventTypeOptions, setEventTypeOptions] = useState<Array<{ label: string; value: string; color?: string; isSystemDefault?: boolean; isActive?: boolean }>>([
    { label: "All event types", value: "all" },
    ...DEFAULT_EVENT_TYPE_OPTIONS,
  ]);
  const [canManageEventTypes, setCanManageEventTypes] = useState(false);
  const [eventTypeDialogVisible, setEventTypeDialogVisible] = useState(false);
  const [eventTypeSaving, setEventTypeSaving] = useState(false);
  const [editingEventTypeId, setEditingEventTypeId] = useState("");
  const [eventTypeName, setEventTypeName] = useState("");
  const [pendingArchiveEventType, setPendingArchiveEventType] = useState<{ label: string; value: string } | null>(null);
  const [workspaceOptions, setWorkspaceOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [homeOptions, setHomeOptions] = useState<Array<{ label: string; value: string }>>([
    { label: "All homes", value: "all" },
  ]);
  const [residentOptions, setResidentOptions] = useState<Array<{ label: string; value: string; homeId?: string }>>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date>(new Date());
  const [eventDialogVisible, setEventDialogVisible] = useState(false);
  const [eventDialogMode, setEventDialogMode] = useState<"create" | "edit">("create");
  const [editScope, setEditScope] = useState("entire_series");
  const [editingEventContext, setEditingEventContext] = useState<any | null>(null);
  const [cancelConfirmVisible, setCancelConfirmVisible] = useState(false);
  const [pendingCancelItem, setPendingCancelItem] = useState<any | null>(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [pendingDeleteItem, setPendingDeleteItem] = useState<any | null>(null);
  const [dayEventsDialogVisible, setDayEventsDialogVisible] = useState(false);
  const [selectedDayEventsDate, setSelectedDayEventsDate] = useState<Date | null>(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState<any[]>([]);
  const [historyDialogVisible, setHistoryDialogVisible] = useState(false);
  const [selectedHistoryEvent, setSelectedHistoryEvent] = useState<any | null>(null);
  const [eventHistoryItems, setEventHistoryItems] = useState<any[]>([]);
  const [quickPreviewVisible, setQuickPreviewVisible] = useState(false);
  const [quickPreviewItem, setQuickPreviewItem] = useState<any | null>(null);
  const [quickPreviewPosition, setQuickPreviewPosition] = useState({ top: 0, left: 0 });
  const [detailsDialogVisible, setDetailsDialogVisible] = useState(false);
  const [selectedDetailsItem, setSelectedDetailsItem] = useState<any | null>(null);
  const [eventForm, setEventForm] = useState({ ...EMPTY_EVENT_FORM });
  const quickPreviewCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const quickPreviewPopupRef = useRef<HTMLDivElement | null>(null);

  const getLosAngelesDateParts = (value: Date) => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: LOS_ANGELES_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(value);

    const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return {
      year: lookup.year || "0000",
      month: lookup.month || "00",
      day: lookup.day || "00",
    };
  };

  const getLosAngelesDayKey = (value?: string | Date | null) => {
    if (!value) {
      return "";
    }

    const nextDate = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(nextDate.getTime())) {
      return "";
    }

    const parts = getLosAngelesDateParts(nextDate);
    return `${parts.year}-${parts.month}-${parts.day}`;
  };

  const formatCalendarDateTime = (isoValue?: string | null) => {
    if (!isoValue) {
      return "No date set";
    }

    const nextDate = new Date(isoValue);
    if (Number.isNaN(nextDate.getTime())) {
      return isoValue;
    }

    return new Intl.DateTimeFormat("en-US", {
      timeZone: LOS_ANGELES_TIME_ZONE,
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(nextDate);
  };

  const formatHistoryMetadataLabel = (key: string) => {
    switch (key) {
      case "recordKeyChanged":
        return "Time Moved";
      case "startAt":
        return "Start";
      case "endAt":
        return "End";
      case "status":
        return "Status";
      default:
        return key.replace(/([A-Z])/g, " $1").replace(/_/g, " ").replace(/^./, (value) => value.toUpperCase());
    }
  };

  const formatHistoryMetadataValue = (key: string, value: unknown) => {
    if (value === null || typeof value === "undefined" || value === "") {
      return "None";
    }

    if (key === "startAt" || key === "endAt") {
      return formatCalendarDateTime(String(value));
    }

    if (key === "recordKeyChanged") {
      return value === true ? "Yes" : "No";
    }

    if (key === "status") {
      const normalizedStatus = String(value).trim();
      return normalizedStatus ? normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1) : "Unknown";
    }

    return String(value);
  };

  const clearQuickPreviewCloseTimeout = () => {
    if (quickPreviewCloseTimeoutRef.current) {
      clearTimeout(quickPreviewCloseTimeoutRef.current);
      quickPreviewCloseTimeoutRef.current = null;
    }
  };

  const openQuickPreview = (item: any, target?: EventTarget | null) => {
    clearQuickPreviewCloseTimeout();
    if (target instanceof HTMLElement) {
      const rect = target.getBoundingClientRect();
      const popupWidth = Math.min(360, Math.max(280, Math.floor(window.innerWidth * 0.28)));
      const estimatedHeight = 320;
      const gap = 14;
      const canOpenRight = rect.right + gap + popupWidth <= window.innerWidth - 12;
      const preferredLeft = canOpenRight
        ? rect.right + gap
        : Math.max(12, rect.left - popupWidth - gap);
      const preferredTop = Math.min(
        Math.max(12, rect.top - 6),
        Math.max(12, window.innerHeight - estimatedHeight - 12),
      );
      setQuickPreviewPosition({
        top: preferredTop,
        left: preferredLeft,
      });
    }
    setQuickPreviewItem(item);
    setQuickPreviewVisible(true);
  };

  const closeQuickPreview = () => {
    clearQuickPreviewCloseTimeout();
    setQuickPreviewVisible(false);
  };

  const scheduleQuickPreviewClose = () => {
    clearQuickPreviewCloseTimeout();
    quickPreviewCloseTimeoutRef.current = setTimeout(() => {
      setQuickPreviewVisible(false);
    }, 180);
  };

  useEffect(() => () => {
    clearQuickPreviewCloseTimeout();
  }, []);

  useEffect(() => {
    if (!quickPreviewVisible || !quickPreviewPopupRef.current) {
      return;
    }

    const popupRect = quickPreviewPopupRef.current.getBoundingClientRect();
    const viewportPadding = 12;
    const nextLeft = Math.min(
      Math.max(viewportPadding, quickPreviewPosition.left),
      Math.max(viewportPadding, window.innerWidth - popupRect.width - viewportPadding),
    );
    const nextTop = Math.min(
      Math.max(viewportPadding, quickPreviewPosition.top),
      Math.max(viewportPadding, window.innerHeight - popupRect.height - viewportPadding),
    );

    if (nextLeft !== quickPreviewPosition.left || nextTop !== quickPreviewPosition.top) {
      setQuickPreviewPosition({
        top: nextTop,
        left: nextLeft,
      });
    }
  }, [quickPreviewItem, quickPreviewPosition.left, quickPreviewPosition.top, quickPreviewVisible]);

  const formatCalendarDayLabel = (value: Date, options?: Intl.DateTimeFormatOptions) => (
    new Intl.DateTimeFormat("en-US", {
      timeZone: LOS_ANGELES_TIME_ZONE,
      month: "short",
      day: "numeric",
      ...options,
    }).format(value)
  );

  const getLosAngelesTimeParts = (value?: string | Date | null) => {
    if (!value) {
      return { hour: "09", minute: "00", second: "00" };
    }

    const nextDate = value instanceof Date ? value : new Date(value);
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: LOS_ANGELES_TIME_ZONE,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(nextDate);
    const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));

    return {
      hour: lookup.hour || "09",
      minute: lookup.minute || "00",
      second: lookup.second || "00",
    };
  };

  const getTimeSlotValue = (value?: string | Date | null) => {
    const timeParts = getLosAngelesTimeParts(value);
    const roundedMinutes = Math.round(Number(timeParts.minute || "0") / 15) * 15;
    const adjustedHour = roundedMinutes === 60 ? (Number(timeParts.hour || "0") + 1) % 24 : Number(timeParts.hour || "0");
    const adjustedMinute = roundedMinutes === 60 ? 0 : roundedMinutes;
    return `${String(adjustedHour).padStart(2, "0")}:${String(adjustedMinute).padStart(2, "0")}:00`;
  };

  const buildLosAngelesIsoForDayKey = (dayKey: string, timeParts: { hour: string; minute: string; second: string }) => {
    const offset = getLosAngelesOffsetSuffix(new Date(`${dayKey}T12:00:00Z`));
    return new Date(`${dayKey}T${timeParts.hour}:${timeParts.minute}:${timeParts.second}${offset}`).toISOString();
  };

  const roundDateToQuarterHour = (value: Date) => {
    const nextDate = new Date(value);
    nextDate.setSeconds(0, 0);
    const minutes = nextDate.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 15) * 15;
    if (roundedMinutes === 60) {
      nextDate.setHours(nextDate.getHours() + 1, 0, 0, 0);
    } else {
      nextDate.setMinutes(roundedMinutes, 0, 0);
    }
    return nextDate;
  };

  const getRoundedFutureStartAt = () => roundDateToQuarterHour(new Date());

  const isSameLosAngelesDay = (left?: Date | string | null, right?: Date | string | null) => {
    if (!left || !right) {
      return false;
    }

    return getLosAngelesDayKey(left) === getLosAngelesDayKey(right);
  };

  const clampStartAtToFuture = (value?: Date | string | null) => {
    const roundedNow = getRoundedFutureStartAt();
    if (!value) {
      return roundedNow;
    }

    const resolvedDate = value instanceof Date ? new Date(value) : new Date(value);
    if (Number.isNaN(resolvedDate.getTime())) {
      return roundedNow;
    }

    return resolvedDate.getTime() < roundedNow.getTime() ? roundedNow : resolvedDate;
  };

  const getVisibleRange = () => {
    if (selectedCalendarView === "day") {
      const dayStart = new Date(selectedCalendarDate);
      const dayEnd = new Date(selectedCalendarDate);
      dayStart.setHours(0, 0, 0, 0);
      dayEnd.setHours(23, 59, 59, 999);
      return {
        start: dayStart.toISOString(),
        end: dayEnd.toISOString(),
      };
    }

    if (selectedCalendarView === "week") {
      const weekStart = startOfWeek(selectedCalendarDate);
      const weekEnd = addDays(weekStart, 6);
      weekEnd.setHours(23, 59, 59, 999);
      return {
        start: weekStart.toISOString(),
        end: weekEnd.toISOString(),
      };
    }

    const monthDays = getMonthGridDays(selectedCalendarDate);
    const firstDay = new Date(monthDays[0]);
    const lastDay = new Date(monthDays[monthDays.length - 1]);
    firstDay.setHours(0, 0, 0, 0);
    lastDay.setHours(23, 59, 59, 999);
    return {
      start: firstDay.toISOString(),
      end: lastDay.toISOString(),
    };
  };

  const refreshWorkspaceEvents = async (workspaceId: string) => {
    if (!workspaceId) {
      setEvents([]);
      return;
    }

    const visibleRange = getVisibleRange();
    const calendarResponse = await backendAPIStore.getSharedCalendarEvents({
      workspaceId,
      start: visibleRange.start,
      end: visibleRange.end,
    });
    setEvents(Array.isArray(calendarResponse?.items) ? calendarResponse.items : []);
  };

  useEffect(() => {
    const loadSharedCalendar = async () => {
      setLoading(true);
      try {
        await backendAPIStore.init();
        const lookups = await backendAPIStore.getSharedCalendarLookups();
        const resolvedWorkspaceOptions = Array.isArray(lookups?.workspaceOptions) ? lookups.workspaceOptions : [];
        const resolvedWorkspaceId = resolvedWorkspaceOptions[0]?.value || "";
        const resolvedEventTypeOptions = [
          { label: "All event types", value: "all" },
          ...((Array.isArray(lookups?.eventTypeOptions) ? lookups.eventTypeOptions : []).map((option: any) => ({
            label: option.label,
            value: option.value,
            color: option.color,
            isSystemDefault: option.isSystemDefault === true,
            isActive: option.isActive !== false,
          }))),
        ];
        const lookupHomeOptions = Array.isArray(lookups?.homeOptions) ? lookups.homeOptions : [];
        const lookupProviderOptions = Array.isArray(lookups?.providerOptions) ? lookups.providerOptions : [];
        const resolvedScopeOptions = isClinicalStaffRole
          ? Array.from(new Map(
            [...lookupProviderOptions, ...lookupHomeOptions]
              .map((option: any) => [String(option?.value || "").trim(), option]),
          ).values()).filter((option: any) => option?.value)
          : lookupHomeOptions;

        setWorkspaceOptions(resolvedWorkspaceOptions);
        setEventTypeOptions(resolvedEventTypeOptions);
        setCanManageEventTypes(lookups?.canManageEventTypes === true);
        setHomeOptions([
          { label: isClinicalStaffRole ? "All providers and homes" : "All homes", value: "all" },
          ...(resolvedScopeOptions.map((option: any) => ({
            label: option.label,
            value: option.value,
          }))),
        ]);
        setResidentOptions(Array.isArray(lookups?.residentOptions) ? lookups.residentOptions : []);
        setSelectedWorkspaceId(resolvedWorkspaceId);
      } catch (error) {
        console.error("Failed to load shared calendar", error);
        setWorkspaceOptions([]);
        setHomeOptions([{ label: "All homes", value: "all" }]);
        setResidentOptions([]);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    void loadSharedCalendar();
  }, [backendAPIStore, isClinicalStaffRole, userRoleType]);

  useEffect(() => {
    const loadWorkspaceEvents = async () => {
      if (!selectedWorkspaceId) {
        setEvents([]);
        return;
      }

      setLoading(true);
      try {
        await backendAPIStore.init();
        await refreshWorkspaceEvents(selectedWorkspaceId);
      } catch (error) {
        console.error("Failed to load shared calendar events", error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    void loadWorkspaceEvents();
  }, [backendAPIStore, selectedCalendarDate, selectedCalendarView, selectedWorkspaceId]);

  const filteredEvents = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return events.filter((item) => {
      const normalizedEventType = String(item.eventType || "meeting");
      if (selectedEventType !== "all" && normalizedEventType !== selectedEventType) {
        return false;
      }

      if (selectedHomeId !== "all" && item.homeId !== selectedHomeId) {
        return false;
      }

      if (selectedResidentFilterId !== "all" && item.residentId !== selectedResidentFilterId) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchPool = [
        item.title,
        item.description,
        item.location,
        normalizedEventType,
        item.homeName,
        item.homeAcronym,
        getHomeAcronym(item.homeName),
        item.residentName,
      ].map((value) => String(value || "").toLowerCase());

      return searchPool.some((value) => value.includes(normalizedSearch));
    });
  }, [events, searchValue, selectedEventType, selectedHomeId, selectedResidentFilterId]);

  const getEventTypeAppearance = (eventType: string) => {
    const configuredOption = eventTypeOptions.find((option) => option.value === eventType);
    if (configuredOption?.color) {
      return {
        tagSeverity: "info" as const,
        accent: configuredOption.color,
        background: `linear-gradient(180deg, ${configuredOption.color}12 0%, #ffffff 100%)`,
      };
    }

    switch (String(eventType || "").toLowerCase()) {
      case "resident_appointment":
        return {
          tagSeverity: "warning" as const,
          accent: "#f59e0b",
          background: "linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)",
        };
      case "document_expiration":
        return {
          tagSeverity: "danger" as const,
          accent: "#dc2626",
          background: "linear-gradient(180deg, #fff1f2 0%, #ffffff 100%)",
        };
      case "general_reminder":
        return {
          tagSeverity: "success" as const,
          accent: "#059669",
          background: "linear-gradient(180deg, #ecfdf5 0%, #ffffff 100%)",
        };
      default:
        return {
          tagSeverity: "info" as const,
          accent: "#2563eb",
          background: "linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)",
        };
    }
  };

  const upcomingItems = useMemo(() => {
    const now = Date.now();
    return [...filteredEvents]
      .filter((item) => {
        const startTime = new Date(item.startAt || "").getTime();
        const normalizedStatus = String(item.status || "scheduled").toLowerCase();
        const isRemovedStatus = normalizedStatus === "cancelled" || normalizedStatus === "deleted";
        return Number.isFinite(startTime)
          && startTime >= now
          && (showCancelledUpcomingOnly ? isRemovedStatus : !isRemovedStatus);
      })
      .sort((first, second) => String(first.startAt || "").localeCompare(String(second.startAt || "")))
      .slice(0, 8);
  }, [filteredEvents, showCancelledUpcomingOnly]);

  const canDeleteCalendarItem = (item: any) => !isInactiveCalendarItem(item);
  const canEditCalendarItem = (item: any) => (
    String(item?.createdByUserId || "").trim() === String(authStore.getUserId() || "").trim()
    || String(item?.createdByName || "").trim() === String(authStore.getUserName() || "").trim()
  );
  const canManageCalendarItem = (item: any) => canEditCalendarItem(item) && canDeleteCalendarItem(item);
  const openCalendarDetails = (item: any) => {
    setSelectedDetailsItem(item);
    setDetailsDialogVisible(true);
  };
  const getCalendarItemStatus = (item: any) => String(item?.status || "scheduled").trim().toLowerCase() || "scheduled";
  const isCancelledCalendarItem = (item: any) => getCalendarItemStatus(item) === "cancelled";
  const isDeletedCalendarItem = (item: any) => getCalendarItemStatus(item) === "deleted";
  const isInactiveCalendarItem = (item: any) => isCancelledCalendarItem(item) || isDeletedCalendarItem(item);
  const formatCalendarStatusLabel = (item: any) => {
    const normalizedStatus = getCalendarItemStatus(item);
    if (normalizedStatus === "cancelled") {
      return "Cancelled";
    }
    if (normalizedStatus === "deleted") {
      return "Deleted";
    }
    return "Scheduled";
  };
  const getCalendarItemTextStyle = (item: any, accentColor?: string) => (
    isInactiveCalendarItem(item)
      ? {
        color: accentColor || "#b91c1c",
        textDecoration: "line-through",
        textDecorationThickness: "2px",
        textDecorationColor: accentColor || "#b91c1c",
      }
      : undefined
  );
  const getCalendarHoverCreateButtonStyle = (isHoveredDay: boolean): React.CSSProperties => ({
    opacity: isHoveredDay ? 1 : 0,
    pointerEvents: isHoveredDay ? "auto" : "none",
    transition: "opacity 120ms ease, transform 120ms ease, box-shadow 120ms ease",
    color: "#ffffff",
    background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    border: "1px solid #b45309",
    boxShadow: isHoveredDay ? "0 10px 18px rgba(217, 119, 6, 0.28)" : "none",
    transform: isHoveredDay ? "scale(1)" : "scale(0.92)",
  });
  const getCalendarItemKey = (item: any, fallbackIndex?: number) => {
    const occurrenceId = String(item?.occurrenceId || "").trim();
    if (occurrenceId) {
      return occurrenceId;
    }
    const eventId = String(item?.eventId || "").trim();
    const sourceEventId = String(item?.sourceEventId || "").trim();
    const startAt = String(item?.startAt || "").trim();
    const residentId = String(item?.residentId || "").trim();
    const homeId = String(item?.homeId || "").trim();
    const fallbackSuffix = fallbackIndex != null ? `-${fallbackIndex}` : "";
    return [sourceEventId || eventId || "calendar-item", startAt || "no-start", residentId || homeId || "no-scope"].join(":") + fallbackSuffix;
  };

  const renderUpcomingTags = (item: any) => {
    const appearance = getEventTypeAppearance(item.eventType || "meeting");
    const normalizedStatus = getCalendarItemStatus(item);
    return (
      <div className="flex flex-wrap gap-2">
        <Tag
          value={getEventTypeLabel(item.eventType || "meeting")}
          severity={appearance.tagSeverity}
        />
        <Tag
          value={formatCalendarStatusLabel(item)}
          severity={normalizedStatus === "scheduled" ? "success" : "danger"}
        />
      </div>
    );
  };

  const renderUpcomingLinkage = (item: any) => (
    <div className="flex flex-wrap gap-2">
      {item.residentName ? (
        <Chip label={item.residentName} />
      ) : item.homeName ? (
        <Chip label={item.homeAcronym || getHomeAcronym(item.homeName)} />
      ) : null}
    </div>
  );

  const renderCreatedByLabel = (item: any) => (
    <span style={{ color: "#596F80" }}>
      {item.createdByName || "Unknown user"}
    </span>
  );

  const renderUpcomingActions = (item: any) => (
    <div className="flex flex-nowrap justify-content-end gap-2" style={{ whiteSpace: "nowrap" }}>
      <Button
        label="View Details"
        icon="pi pi-eye"
        size="small"
        rounded
        outlined
        severity="warning"
        onClick={() => openCalendarDetails(item)}
        tooltip="View Details"
      />
      {canEditCalendarItem(item) ? (
        <Button
          label="Edit"
          icon="pi pi-pencil"
          size="small"
          text
          onClick={() => openEditDialog(item)}
          tooltip="Edit"
        />
      ) : null}
      <Button
        icon="pi pi-history"
        size="small"
        text
        rounded
        onClick={() => void loadEventHistory(item)}
        tooltip="History"
      />
      {!isInactiveCalendarItem(item) ? (
        <Button
          icon="pi pi-times"
          size="small"
          text
          rounded
          severity="danger"
          onClick={() => requestCancelEvent(item)}
          disabled={saving}
          tooltip="Cancel"
        />
      ) : null}
      {canManageCalendarItem(item) ? (
        <Button
          icon="pi pi-trash"
          size="small"
          text
          rounded
          severity="danger"
          onClick={() => requestDeleteEvent(item)}
          disabled={saving}
          tooltip="Delete"
        />
      ) : null}
    </div>
  );

  const renderDayEventsActions = (item: any) => (
    <div className="flex flex-nowrap justify-content-end gap-2" style={{ whiteSpace: "nowrap" }}>
      <Button
        icon="pi pi-eye"
        size="small"
        rounded
        outlined
        severity="warning"
        onClick={() => openCalendarDetails(item)}
        tooltip="View Details"
      />
      {canEditCalendarItem(item) ? (
        <Button
          icon="pi pi-pencil"
          size="small"
          text
          rounded
          onClick={() => {
            setDayEventsDialogVisible(false);
            openEditDialog(item);
          }}
          tooltip="Edit"
        />
      ) : null}
      <Button
        icon="pi pi-history"
        size="small"
        text
        rounded
        onClick={() => void loadEventHistory(item)}
        tooltip="History"
      />
      {!isInactiveCalendarItem(item) ? (
        <Button
          icon="pi pi-times"
          size="small"
          text
          rounded
          severity="danger"
          onClick={() => requestCancelEvent(item)}
          disabled={saving}
          tooltip="Cancel"
        />
      ) : null}
      {canManageCalendarItem(item) ? (
        <Button
          icon="pi pi-trash"
          size="small"
          text
          rounded
          severity="danger"
          onClick={() => requestDeleteEvent(item)}
          disabled={saving}
          tooltip="Delete"
        />
      ) : null}
    </div>
  );

  const eventsByDayKey = useMemo(() => {
    return filteredEvents.reduce((accumulator: Record<string, any[]>, item) => {
      const dayKey = getLosAngelesDayKey(item.startAt);
      if (!dayKey) {
        return accumulator;
      }

      if (!accumulator[dayKey]) {
        accumulator[dayKey] = [];
      }

      accumulator[dayKey].push(item);
      accumulator[dayKey].sort((first, second) => String(first.startAt || "").localeCompare(String(second.startAt || "")));
      return accumulator;
    }, {});
  }, [filteredEvents]);

  const monthGridDays = useMemo(() => getMonthGridDays(selectedCalendarDate), [selectedCalendarDate]);

  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(selectedCalendarDate);
    return Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
  }, [selectedCalendarDate]);

  const activeCalendarLabel = useMemo(() => {
    if (selectedCalendarView === "day") {
      return formatCalendarDayLabel(selectedCalendarDate, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }

    if (selectedCalendarView === "week") {
      const firstDay = weekDays[0];
      const lastDay = weekDays[6];
      return `${formatCalendarDayLabel(firstDay)} - ${formatCalendarDayLabel(lastDay, { month: "short", day: "numeric", year: "numeric" })}`;
    }

    return new Intl.DateTimeFormat("en-US", {
      timeZone: LOS_ANGELES_TIME_ZONE,
      month: "long",
      year: "numeric",
    }).format(selectedCalendarDate);
  }, [selectedCalendarDate, selectedCalendarView, weekDays]);

  const monthBreadcrumbLabel = useMemo(() => (
    new Intl.DateTimeFormat("en-US", {
      timeZone: LOS_ANGELES_TIME_ZONE,
      month: "long",
      year: "numeric",
    }).format(selectedCalendarDate)
  ), [selectedCalendarDate]);

  const formResidentOptions = useMemo(() => {
    if (!eventForm.homeId) {
      return residentOptions;
    }

    return residentOptions.filter((option) => !option.homeId || option.homeId === eventForm.homeId);
  }, [eventForm.homeId, residentOptions]);

  const availableHomeOptions = useMemo(
    () => homeOptions.filter((option) => option.value !== "all"),
    [homeOptions],
  );

  const resolveHomeOptionLabel = (value?: string | null) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) {
      return "";
    }

    return availableHomeOptions.find((option) => option.value === normalizedValue)?.label
      || editingEventContext?.homeName
      || normalizedValue;
  };

  const filterGroupLabel = useMemo(() => {
    if (isAdminUser || userRoleType === ROLE_TYPE_PROVIDER) {
      return "Homes";
    }
    if (userRoleType === ROLE_TYPE_CAREGIVER) {
      return "Shared Homes";
    }
    if (isClinicalStaffRole) {
      return "Providers / Homes";
    }
    return "Groups";
  }, [isAdminUser, isClinicalStaffRole, userRoleType]);

  const groupedScopeOptions = useMemo(() => {
    const groups = availableHomeOptions.map((homeOption) => ({
      label: homeOption.label,
      items: [
        { label: homeOption.label, value: `home:${homeOption.value}` },
        ...residentOptions
          .filter((residentOption) => residentOption.homeId === homeOption.value)
          .map((residentOption) => ({
            label: residentOption.label,
            value: `resident:${residentOption.value}`,
          })),
      ],
    }));

    return [
      {
        label: "All",
        items: [{ label: "All homes and residents", value: "all" }],
      },
      ...groups,
    ];
  }, [availableHomeOptions, residentOptions]);

  const selectedScopeValue = useMemo(() => {
    if (selectedResidentFilterId !== "all") {
      return `resident:${selectedResidentFilterId}`;
    }
    if (selectedHomeId !== "all") {
      return `home:${selectedHomeId}`;
    }
    return "all";
  }, [selectedHomeId, selectedResidentFilterId]);

  const scopeOptionGroupTemplate = (option: any) => (
    <div
      style={{
        height: option?.label === "All" ? "0.2rem" : "0.65rem",
        marginTop: option?.label === "All" ? 0 : "0.35rem",
        borderTop: option?.label === "All" ? "none" : "1px solid #e2e8f0",
      }}
      aria-hidden="true"
    />
  );

  const scopeItemTemplate = (option: any) => {
    const optionValue = String(option?.value || "");
    const isResidentOption = optionValue.startsWith("resident:");

    return (
      <div
        data-calendar-scope-value={optionValue}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.55rem",
          paddingLeft: isResidentOption ? "1rem" : "0.15rem",
          minWidth: 0,
        }}
      >
        <span
          className={isResidentOption ? "pi pi-user" : "pi pi-home"}
          style={{ color: isResidentOption ? "#475569" : "#b45309", fontSize: "0.85rem" }}
        />
        <span style={{ whiteSpace: "normal", lineHeight: 1.35 }}>{option.label}</span>
      </div>
    );
  };

  const eventFormScopeOptionTemplate = (option: any) => {
    const optionValue = String(option?.value || "");
    const isResidentOption = optionValue.startsWith("resident:");

    return (
      <div
        data-calendar-scope-value={optionValue}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.55rem",
          minWidth: 0,
          paddingLeft: isResidentOption ? "1rem" : "0.15rem",
        }}
      >
        <span
          className={isResidentOption ? "pi pi-user" : isClinicalStaffRole ? "pi pi-briefcase" : "pi pi-home"}
          style={{
            color: isResidentOption ? "#475569" : isClinicalStaffRole ? "#185da2" : "#b45309",
            fontSize: "0.85rem",
          }}
        />
        <span style={{ whiteSpace: "normal", lineHeight: 1.35 }}>{option?.label}</span>
      </div>
    );
  };

  const eventFormScopeOptionGroupTemplate = () => (
    <div
      style={{
        height: "0.65rem",
        marginTop: "0.35rem",
        borderTop: "1px solid #e2e8f0",
      }}
      aria-hidden="true"
    />
  );

  const eventFormScopeOptions = useMemo(() => (
    availableHomeOptions.map((homeOption) => ({
      label: homeOption.label,
      items: [
        { label: homeOption.label, value: `home:${homeOption.value}` },
        ...residentOptions
          .filter((residentOption) => residentOption.homeId === homeOption.value)
          .map((residentOption) => ({
            label: residentOption.label,
            value: `resident:${residentOption.value}`,
          })),
      ],
    }))
  ), [availableHomeOptions, residentOptions]);

  const selectedEventScopeValue = useMemo(() => {
    if (eventForm.residentId) {
      return `resident:${eventForm.residentId}`;
    }
    if (eventForm.homeId) {
      return `home:${eventForm.homeId}`;
    }
    return "";
  }, [eventForm.homeId, eventForm.residentId]);

  const eventFormScopePlaceholder = useMemo(() => {
    if (isClinicalStaffRole) {
      return "Select provider, home, or resident";
    }
    if (userRoleType === ROLE_TYPE_CAREGIVER) {
      return "Select shared home or resident";
    }
    return "Select home or resident";
  }, [isClinicalStaffRole, userRoleType]);

  const eventFormScopeLabel = useMemo(() => {
    if (isClinicalStaffRole) {
      return "Provider / Home / Resident";
    }
    if (userRoleType === ROLE_TYPE_CAREGIVER) {
      return "Shared Home / Resident";
    }
    return "Home / Resident";
  }, [isClinicalStaffRole, userRoleType]);

  const eventFormScopeValueTemplate = (option: any, props: any) => {
    if (!option) {
      return <span style={{ color: "#94a3b8" }}>{props.placeholder}</span>;
    }

    const optionValue = String(option?.value || "");
    if (optionValue.startsWith("resident:")) {
      const residentId = optionValue.replace("resident:", "");
      const residentOption = residentOptions.find((item) => item.value === residentId);
      return eventFormScopeOptionTemplate({
        ...option,
        label: residentOption?.label || option?.label,
      });
    }

    return eventFormScopeOptionTemplate({
      ...option,
      label: resolveHomeOptionLabel(optionValue.replace("home:", "")),
    });
  };

  const applyEventFormScopeValue = (value: string) => {
    const normalizedValue = String(value || "");
    if (!normalizedValue) {
      setEventForm((current) => ({
        ...current,
        homeId: "",
        residentId: "",
      }));
      return;
    }

    if (normalizedValue.startsWith("resident:")) {
      const residentId = normalizedValue.replace("resident:", "");
      const selectedResidentOption = residentOptions.find((option) => option.value === residentId);
      setEventForm((current) => ({
        ...current,
        homeId: selectedResidentOption?.homeId || current.homeId,
        residentId,
      }));
      return;
    }

    if (normalizedValue.startsWith("home:")) {
      const homeId = normalizedValue.replace("home:", "");
      setEventForm((current) => ({
        ...current,
        homeId,
        residentId: "",
      }));
    }
  };

  const hasHomeOptions = availableHomeOptions.length > 0;

  const isResidentAppointmentEvent = eventForm.eventType === "resident_appointment";
  const isDocumentExpirationEvent = eventForm.eventType === "document_expiration";
  const shouldHideRepeatSection = isDocumentExpirationEvent;
  const shouldShowStartTimePicker = !isDocumentExpirationEvent;
  const startDateLabel = isDocumentExpirationEvent
    ? "Expiry Date"
    : isResidentAppointmentEvent
      ? "PickUp Date"
      : "Start Date";
  const startTimeLabel = isResidentAppointmentEvent ? "PickUp Time" : "Start Time";
  const normalizedStartAt = clampStartAtToFuture(eventForm.startAt);
  const selectedStartTimeValue = getTimeSlotValue(normalizedStartAt);

  const resolveDefaultCreateHomeId = () => {
    if (selectedHomeId !== "all" && availableHomeOptions.some((option) => option.value === selectedHomeId)) {
      return selectedHomeId;
    }

    if (availableHomeOptions.length === 1) {
      return availableHomeOptions[0].value;
    }

    return "";
  };

  const repeatUntilMinDate = useMemo(() => {
    const nextDate = addDays(new Date(), 1);
    nextDate.setHours(0, 0, 0, 0);
    return nextDate;
  }, []);

  const startDateMinDate = useMemo(
    () => new Date(buildLosAngelesIsoForDayKey(getLosAngelesDayKey(new Date()), {
      hour: "00",
      minute: "00",
      second: "00",
    })),
    [],
  );

  const availableStartTimeOptions = useMemo(() => {
    const roundedNow = getRoundedFutureStartAt();
    if (!isSameLosAngelesDay(normalizedStartAt, roundedNow)) {
      return START_TIME_OPTIONS;
    }

    const earliestTimeValue = getTimeSlotValue(roundedNow);
    return START_TIME_OPTIONS.filter((option) => option.value >= earliestTimeValue);
  }, [normalizedStartAt]);

  const openCreateDialog = () => {
    setEventDialogMode("create");
    setEditScope("entire_series");
    setEditingEventContext(null);
    const defaultHomeId = resolveDefaultCreateHomeId();
    const defaultStartAt = getRoundedFutureStartAt();
    setEventForm({
      ...EMPTY_EVENT_FORM,
      eventType: selectedEventType !== "all" ? selectedEventType : "meeting",
      homeId: defaultHomeId,
      startAt: defaultStartAt,
      endAt: null,
    });
    setEventDialogVisible(true);
  };

  useEffect(() => {
    const handleCreateRequest = () => {
      openCreateDialog();
    };

    window.addEventListener("shared-calendar:request-create", handleCreateRequest);
    return () => {
      window.removeEventListener("shared-calendar:request-create", handleCreateRequest);
    };
  }, [openCreateDialog]);

  const openCreateDialogForDay = (value: Date) => {
    setEventDialogMode("create");
    setEditScope("entire_series");
    setEditingEventContext(null);
    const defaultHomeId = resolveDefaultCreateHomeId();
    const roundedNow = getRoundedFutureStartAt();
    const dayKey = getLosAngelesDayKey(value);
    const timeParts = getLosAngelesTimeParts(roundedNow);
    const defaultStartAt = clampStartAtToFuture(new Date(buildLosAngelesIsoForDayKey(dayKey, timeParts)));

    setEventForm({
      ...EMPTY_EVENT_FORM,
      eventType: selectedEventType !== "all" ? selectedEventType : "meeting",
      homeId: defaultHomeId,
      startAt: defaultStartAt,
      endAt: null,
    });
    setEventDialogVisible(true);
  };

  const openDayEventsDialog = (value: Date, items: any[]) => {
    setSelectedDayEventsDate(value);
    setSelectedDayEvents(items);
    setDayEventsDialogVisible(true);
  };

  const requestCancelEvent = (item: any) => {
    if (!item?.eventId) {
      return;
    }
    setPendingCancelItem(item);
    setCancelConfirmVisible(true);
  };

  const requestDeleteEvent = (item: any) => {
    if (!item?.eventId) {
      return;
    }
    setPendingDeleteItem(item);
    setDeleteConfirmVisible(true);
  };

  const closeCancelConfirmDialog = () => {
    if (saving) {
      return;
    }
    setCancelConfirmVisible(false);
    setPendingCancelItem(null);
  };

  const closeDeleteConfirmDialog = () => {
    if (saving) {
      return;
    }
    setDeleteConfirmVisible(false);
    setPendingDeleteItem(null);
  };

  const updateStartDateValue = (value: Date | null | undefined) => {
    if (!value) {
      updateEventFormField("startAt", value);
      return;
    }

    const dayKey = getLosAngelesDayKey(value);
    const timeParts = getLosAngelesTimeParts(normalizedStartAt || getRoundedFutureStartAt());
    updateEventFormField("startAt", clampStartAtToFuture(new Date(buildLosAngelesIsoForDayKey(dayKey, timeParts))));
  };

  const updateStartTimeValue = (value: string) => {
    const selectedDate = normalizedStartAt || getRoundedFutureStartAt();
    const dayKey = getLosAngelesDayKey(selectedDate);
    const [hour = "09", minute = "00", second = "00"] = String(value || "09:00:00").split(":");
    updateEventFormField("startAt", clampStartAtToFuture(new Date(buildLosAngelesIsoForDayKey(dayKey, { hour, minute, second }))));
  };

  const updateScopeFilter = (value: string) => {
    const normalizedValue = String(value || "all");
    if (!normalizedValue || normalizedValue === "all") {
      setSelectedHomeId("all");
      setSelectedResidentFilterId("all");
      return;
    }

    if (normalizedValue.startsWith("resident:")) {
      const residentId = normalizedValue.replace("resident:", "");
      const selectedResidentOption = residentOptions.find((option) => option.value === residentId);
      setSelectedResidentFilterId(residentId || "all");
      setSelectedHomeId(selectedResidentOption?.homeId || "all");
      return;
    }

    if (normalizedValue.startsWith("home:")) {
      setSelectedHomeId(normalizedValue.replace("home:", "") || "all");
      setSelectedResidentFilterId("all");
    }
  };

  const getEventTypeLabel = (eventType: string) => (
    eventTypeOptions.find((option) => option.value === eventType)?.label
    || String(eventType || "meeting").replace(/_/g, " ")
  );

  const openFilterDialog = () => {
    setDraftScopeValue(selectedScopeValue);
    setDraftEventType(selectedEventType);
    setDraftSearchValue(searchValue);
    setDraftActiveOnly(!showCancelledUpcomingOnly);
    setFilterDialogVisible(true);
  };

  const applyFilters = () => {
    updateScopeFilter(draftScopeValue);
    setSelectedEventType(draftEventType);
    setSearchValue(draftSearchValue);
    setShowCancelledUpcomingOnly(!draftActiveOnly);
    setFilterDialogVisible(false);
  };

  const resetFilterDraft = () => {
    setDraftScopeValue("all");
    setDraftEventType("all");
    setDraftSearchValue("");
    setDraftActiveOnly(true);
  };

  const clearFilters = () => {
    resetFilterDraft();
    updateScopeFilter("all");
    setSelectedEventType("all");
    setSearchValue("");
    setShowCancelledUpcomingOnly(false);
  };

  const openCreateEventTypeDialog = () => {
    setEditingEventTypeId("");
    setEventTypeName("");
    setEventTypeDialogVisible(true);
  };

  const openEditEventTypeDialog = (option: { label: string; value: string; color?: string; isSystemDefault?: boolean }) => {
    if (option.value === "all" || option.isSystemDefault) {
      return;
    }
    setEditingEventTypeId(option.value);
    setEventTypeName(option.label);
    setEventTypeDialogVisible(true);
  };

  const saveEventType = async (forceCreate = false) => {
    const normalizedName = eventTypeName.trim();
    if (!normalizedName) {
      toast.current?.show({ severity: "warn", summary: "Name required", detail: "Enter an event type name.", life: 3000 });
      return;
    }

    setEventTypeSaving(true);
    try {
      await backendAPIStore.init();
      const response = editingEventTypeId && !forceCreate
        ? await backendAPIStore.updateSharedCalendarEventType({
          eventTypeId: editingEventTypeId,
          label: normalizedName,
        })
        : await backendAPIStore.createSharedCalendarEventType({
          label: normalizedName,
        });
      if (Array.isArray(response?.eventTypeOptions)) {
        setEventTypeOptions([
          { label: "All event types", value: "all" },
          ...response.eventTypeOptions.map((option: any) => ({
            label: option.label,
            value: option.value,
            color: option.color,
            isSystemDefault: option.isSystemDefault === true,
            isActive: option.isActive !== false,
          })),
        ]);
      }
      if (forceCreate) {
        setEditingEventTypeId("");
        setEventTypeName("");
      } else {
        setEventTypeDialogVisible(false);
      }
      toast.current?.show({ severity: "success", summary: editingEventTypeId && !forceCreate ? "Event type updated" : "Event type added", detail: "The event type is now available in the calendar.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Unable to save event type", detail: error?.message || "Please try again.", life: 4000 });
    } finally {
      setEventTypeSaving(false);
    }
  };

  const requestArchiveEventType = (option: { label: string; value: string; isSystemDefault?: boolean }) => {
    if (option.isSystemDefault || !option.value) {
      return;
    }
    setPendingArchiveEventType({ label: option.label, value: option.value });
  };

  const archiveEventType = async () => {
    if (!pendingArchiveEventType) {
      return;
    }

    setEventTypeSaving(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.archiveSharedCalendarEventType(pendingArchiveEventType.value);
      if (Array.isArray(response?.eventTypeOptions)) {
        setEventTypeOptions([
          { label: "All event types", value: "all" },
          ...response.eventTypeOptions.map((option: any) => ({
            label: option.label,
            value: option.value,
            color: option.color,
            isSystemDefault: option.isSystemDefault === true,
            isActive: option.isActive !== false,
          })),
        ]);
      }
      if (selectedEventType === pendingArchiveEventType.value) {
        setSelectedEventType("all");
      }
      setPendingArchiveEventType(null);
      toast.current?.show({ severity: "success", summary: "Event type archived", detail: "It will no longer appear for new events.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Unable to archive event type", detail: error?.message || "Please try again.", life: 4000 });
    } finally {
      setEventTypeSaving(false);
    }
  };

  const openEditDialog = (item: any) => {
    setEventDialogMode("edit");
    setEditScope(item?.occurrenceId && item?.recurrenceRule ? "this_event" : "entire_series");
    setEditingEventContext(item);
    setEventForm({
      eventId: item.eventId || "",
      eventType: item.eventType || "meeting",
      title: item.title || "",
      description: item.description || "",
      startAt: item.startAt ? new Date(item.startAt) : null,
      endAt: item.endAt ? new Date(item.endAt) : null,
      allDay: item.allDay === true,
      homeId: item.homeId || "",
      residentId: item.residentId || "",
      notificationEmailReminder: item.notificationSettings?.emailReminder || "none",
      repeatEnabled: item.recurrenceRule?.frequency ? "yes" : "no",
      recurrenceFrequency: item.recurrenceRule?.frequency || "none",
      recurrenceInterval: String(item.recurrenceRule?.interval || 1),
      recurrenceUntil: item.recurrenceRule?.until ? new Date(item.recurrenceRule.until) : null,
    });
    setEventDialogVisible(true);
  };

  const closeEventDialog = () => {
    if (saving) {
      return;
    }
    setEventDialogVisible(false);
    setEditScope("entire_series");
    setEditingEventContext(null);
    setEventForm({ ...EMPTY_EVENT_FORM });
  };

  const updateEventFormField = (field: string, value: any) => {
    setEventForm((current) => {
      if (field === "homeId") {
        const nextHomeId = String(value || "");
        const selectedResidentOption = residentOptions.find((option) => option.value === current.residentId);
        return {
          ...current,
          homeId: nextHomeId,
          residentId: selectedResidentOption && selectedResidentOption.homeId && selectedResidentOption.homeId !== nextHomeId
            ? ""
            : current.residentId,
        };
      }

      if (field === "residentId") {
        const selectedResidentOption = residentOptions.find((option) => option.value === value);
        return {
          ...current,
          residentId: value,
          homeId: selectedResidentOption?.homeId || current.homeId,
        };
      }

      if (field === "repeatEnabled") {
        return {
          ...current,
          repeatEnabled: value,
          recurrenceFrequency: value === "yes"
            ? (current.recurrenceFrequency === "none" ? "weekly" : current.recurrenceFrequency)
            : "none",
          recurrenceInterval: value === "yes" ? current.recurrenceInterval : "1",
          recurrenceUntil: value === "yes" ? current.recurrenceUntil : null,
        };
      }

      return {
        ...current,
        [field]: value,
      };
    });
  };

  const loadEventHistory = async (item: any) => {
    if (!item?.eventId || !selectedWorkspaceId) {
      return;
    }

    setSelectedHistoryEvent(item);
    setHistoryDialogVisible(true);
    setHistoryLoading(true);
    try {
      await backendAPIStore.init();
      const historyResponse = await backendAPIStore.getSharedCalendarHistory(item.eventId, selectedWorkspaceId);
      setEventHistoryItems(Array.isArray(historyResponse?.items) ? historyResponse.items : []);
    } catch (error: any) {
      setEventHistoryItems([]);
      toast.current?.show({
        severity: "error",
        summary: "History unavailable",
        detail: error?.message || "Unable to load the event history right now.",
        life: 3500,
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  const moveEventToDay = async (item: any, targetDayKey: string) => {
    if (!item?.eventId || !selectedWorkspaceId || !targetDayKey || saving) {
      return;
    }

    const currentDayKey = getLosAngelesDayKey(item.startAt);
    if (!currentDayKey || currentDayKey === targetDayKey) {
      return;
    }

    const startDate = new Date(item.startAt);
    const endDate = new Date(item.endAt || item.startAt);
    const durationMs = Math.max(endDate.getTime() - startDate.getTime(), 0);
    const startTimeParts = getLosAngelesTimeParts(item.startAt);
    const nextStartAt = buildLosAngelesIsoForDayKey(targetDayKey, startTimeParts);
    const nextEndAt = new Date(new Date(nextStartAt).getTime() + durationMs).toISOString();

    setSaving(true);
    try {
      await backendAPIStore.init();
      if (item.occurrenceId && item.recurrenceRule) {
        await backendAPIStore.applySharedCalendarOccurrenceAction({
          workspaceId: selectedWorkspaceId,
          action: "update_single",
          eventId: item.sourceEventId || item.eventId,
          occurrenceStartAt: item.startAt,
          update: {
            title: item.title,
            description: item.description,
            eventType: item.eventType || "meeting",
            startAt: nextStartAt,
            endAt: nextEndAt,
            allDay: item.allDay === true,
            homeId: item.homeId,
            residentId: item.residentId,
            notificationSettings: item.notificationSettings || null,
          },
        });
      } else {
        await backendAPIStore.updateSharedCalendarEvent({
          workspaceId: selectedWorkspaceId,
          eventId: item.eventId,
          title: item.title,
          description: item.description,
          eventType: item.eventType || "meeting",
          startAt: nextStartAt,
          endAt: nextEndAt,
          allDay: item.allDay === true,
          homeId: item.homeId,
          residentId: item.residentId,
          assignedUserIds: item.assignedUserIds || [],
          attendeeUserIds: item.attendeeUserIds || [],
          recurrenceRule: item.recurrenceRule || null,
          notificationSettings: item.notificationSettings || null,
          status: item.status || "scheduled",
        });
      }
      await refreshWorkspaceEvents(selectedWorkspaceId);
      toast.current?.show({
        severity: "success",
        summary: "Event moved",
        detail: `Moved "${item.title || "calendar item"}" to ${targetDayKey}.`,
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Move failed",
        detail: error?.message || "Unable to move the shared calendar item.",
        life: 3500,
      });
    } finally {
      setSaving(false);
      setDraggingEventId("");
    }
  };

  const saveEvent = async () => {
    if (!selectedWorkspaceId) {
      toast.current?.show({ severity: "warn", summary: "Workspace required", detail: "Choose a workspace before saving.", life: 3000 });
      return;
    }

    if (!String(eventForm.title || "").trim()) {
      toast.current?.show({ severity: "warn", summary: "Title required", detail: "Enter a title for the calendar item.", life: 3000 });
      return;
    }

    if (!eventForm.startAt) {
      toast.current?.show({ severity: "warn", summary: "Start required", detail: "Choose a start date and time.", life: 3000 });
      return;
    }

    setSaving(true);
    try {
      await backendAPIStore.init();
      const recurrenceRule = eventForm.repeatEnabled === "yes" && eventForm.recurrenceFrequency !== "none"
        ? {
          frequency: eventForm.recurrenceFrequency,
          interval: Math.max(1, Number(eventForm.recurrenceInterval) || 1),
          until: eventForm.recurrenceUntil ? eventForm.recurrenceUntil.toISOString() : null,
        }
        : null;
      const notificationSettings = eventForm.notificationEmailReminder !== "none"
        ? {
          emailReminder: eventForm.notificationEmailReminder,
        }
        : null;
      const requestBody = {
        workspaceId: selectedWorkspaceId,
        eventId: eventDialogMode === "edit" ? eventForm.eventId : undefined,
        eventType: eventForm.eventType,
        title: eventForm.title,
        description: eventForm.description,
        startAt: eventForm.startAt.toISOString(),
        endAt: undefined,
        allDay: eventForm.allDay,
        homeId: eventForm.homeId || undefined,
        residentId: eventForm.residentId || undefined,
        notificationSettings,
        recurrenceRule,
      };

      if (eventDialogMode === "edit") {
        if (editingEventContext?.occurrenceId && editingEventContext?.recurrenceRule && editScope === "this_event") {
          await backendAPIStore.applySharedCalendarOccurrenceAction({
            workspaceId: selectedWorkspaceId,
            action: "update_single",
            eventId: editingEventContext.sourceEventId || editingEventContext.eventId,
            occurrenceStartAt: editingEventContext.startAt,
            update: requestBody,
          });
        } else {
          await backendAPIStore.updateSharedCalendarEvent(requestBody);
        }
      } else {
        await backendAPIStore.createSharedCalendarEvent(requestBody);
      }

      await refreshWorkspaceEvents(selectedWorkspaceId);
      setEventDialogVisible(false);
      setEditScope("entire_series");
      setEditingEventContext(null);
      setEventForm({ ...EMPTY_EVENT_FORM });
      toast.current?.show({
        severity: "success",
        summary: eventDialogMode === "edit" ? "Event updated" : "Event created",
        detail: eventDialogMode === "edit"
          ? "Shared calendar event updated successfully."
          : "Shared calendar event created successfully.",
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Save failed",
        detail: error?.message || "Unable to save the shared calendar event.",
        life: 3500,
      });
    } finally {
      setSaving(false);
    }
  };

  const cancelEvent = async (item: any) => {
    if (!item?.eventId) {
      return;
    }

    setSaving(true);
    try {
      await backendAPIStore.init();
      if (item.occurrenceId && item.recurrenceRule) {
        await backendAPIStore.applySharedCalendarOccurrenceAction({
          workspaceId: selectedWorkspaceId,
          action: "cancel_single",
          eventId: item.sourceEventId || item.eventId,
          occurrenceStartAt: item.startAt,
        });
      } else {
        await backendAPIStore.cancelSharedCalendarEvent(item.eventId);
      }
      await refreshWorkspaceEvents(selectedWorkspaceId);
      toast.current?.show({
        severity: "success",
        summary: "Event cancelled",
        detail: "Shared calendar event cancelled successfully.",
        life: 3000,
      });
      setCancelConfirmVisible(false);
      setPendingCancelItem(null);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Cancel failed",
        detail: error?.message || "Unable to cancel the shared calendar event.",
        life: 3500,
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (item: any) => {
    if (!item?.eventId) {
      return;
    }

    setSaving(true);
    try {
      await backendAPIStore.init();
      const recurringSeries = Boolean(item?.recurrenceRule || item?.occurrenceId);
      await backendAPIStore.deleteSharedCalendarEvent(recurringSeries ? (item.sourceEventId || item.eventId) : item.eventId, recurringSeries ? "deleteSeries" : "delete");
      await refreshWorkspaceEvents(selectedWorkspaceId);
      setDeleteConfirmVisible(false);
      setPendingDeleteItem(null);
      setDayEventsDialogVisible(false);
      toast.current?.show({
        severity: "success",
        summary: "Event deleted",
        detail: "Shared calendar event deleted successfully.",
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Delete failed",
        detail: error?.message || "Unable to delete the shared calendar event.",
        life: 3500,
      });
    } finally {
      setSaving(false);
    }
  };

  const resetCalendar = async () => {
    await backendAPIStore.resetSharedCalendar();
    await refreshWorkspaceEvents(selectedWorkspaceId);
    toast.current?.show({ severity: "success", summary: "Calendar reset", detail: "Current and future calendar events were deleted." });
  };

  const shiftCalendarWindow = (direction: -1 | 1) => {
    setSelectedCalendarDate((current) => {
      if (selectedCalendarView === "day") {
        return addDays(current, direction);
      }

      if (selectedCalendarView === "week") {
        return addDays(current, direction * 7);
      }

      return new Date(current.getFullYear(), current.getMonth() + direction, 1);
    });
  };

  const goToAdjacentMonth = (direction: -1 | 1) => {
    setSelectedCalendarView("month");
    setSelectedCalendarDate((current) => new Date(current.getFullYear(), current.getMonth() + direction, 1));
  };

  const eventDialogFooter = (
    <div className="flex justify-content-end gap-2">
      {eventDialogMode === "edit" && !isInactiveCalendarItem(editingEventContext) ? (
        <Button
          label="Cancel Event"
          icon="pi pi-times"
          severity="danger"
          text
          onClick={() => {
            setEventDialogVisible(false);
            requestCancelEvent(editingEventContext);
          }}
          disabled={saving}
        />
      ) : null}
      {eventDialogMode === "edit" && editingEventContext && canManageCalendarItem(editingEventContext) ? (
        <Button
          label="Delete Event"
          icon="pi pi-trash"
          severity="danger"
          text
          onClick={() => {
            setEventDialogVisible(false);
            requestDeleteEvent(editingEventContext);
          }}
          disabled={saving}
        />
      ) : null}
      <Button label="Close" severity="secondary" text onClick={closeEventDialog} disabled={saving} />
      <Button
        label={eventDialogMode === "edit" ? "Save Changes" : "Create Event"}
        severity="warning"
        rounded
        onClick={saveEvent}
        loading={saving}
      />
    </div>
  );

  const historyDialogFooter = (
    <div className="flex justify-content-end">
      <Button label="Close" severity="secondary" text onClick={() => setHistoryDialogVisible(false)} />
    </div>
  );

  const dayEventsDialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button label="Close" severity="secondary" text onClick={() => setDayEventsDialogVisible(false)} />
      <Button
        label="New Event"
        icon="pi pi-plus"
        severity="warning"
        rounded
        onClick={() => {
          const fallbackDate = selectedDayEventsDate || new Date();
          setDayEventsDialogVisible(false);
          openCreateDialogForDay(fallbackDate);
        }}
      />
    </div>
  );

  const cancelConfirmDialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Keep Event"
        severity="secondary"
        text
        onClick={closeCancelConfirmDialog}
        disabled={saving}
      />
      <Button
        label="Cancel Event"
        icon="pi pi-times"
        severity="danger"
        rounded
        onClick={() => pendingCancelItem ? void cancelEvent(pendingCancelItem) : undefined}
        loading={saving}
      />
    </div>
  );

  const deleteConfirmDialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Keep Event"
        severity="secondary"
        text
        onClick={closeDeleteConfirmDialog}
        disabled={saving}
      />
      <Button
        label="Delete Event"
        icon="pi pi-trash"
        severity="danger"
        rounded
        onClick={() => pendingDeleteItem ? void deleteEvent(pendingDeleteItem) : undefined}
        loading={saving}
      />
    </div>
  );

  if (!canAccessSharedCalendar) {
    return (
      <div style={{ padding: "1.25rem" }}>
        <Card>
          <Unauthorized />
        </Card>
      </div>
    );
  }

  return (
    <div className="container-fluid p-3">
      <Toast ref={toast} />
      <div style={{ width: "100%" }}>
        <div className="mb-4">
          <h2 className="m-0">Shared Calendar</h2>
          <div className="mt-2" style={{ ...sharedCalendarCardSubtleTextStyle, maxWidth: "900px" }}>
            A shared calendar between Staff Members for meetings, resident appointments, staff certification expirations, and
            facility reminders.
          </div>
        </div>

        <div className="grid">
          <div className="col-12">
            <Card style={{ ...sharedCalendarSurfaceCardStyle, height: "100%" }}>
              <div className="mb-3 flex align-items-center justify-content-between gap-3 flex-wrap">
                <div>
                  <div style={sharedCalendarCardHeaderStyle}>Upcoming Items</div>
                  <div className="mt-1" style={sharedCalendarCardSubtleTextStyle}>
                    Track the next items without taking over the full page.
                  </div>
                </div>
                <div className="flex align-items-center gap-2">
                  <Button
                    type="button"
                    icon="pi pi-filter"
                    rounded
                    outlined
                    severity="secondary"
                    aria-label="Open calendar filters"
                    tooltip="Filters"
                    tooltipOptions={{ position: "top" }}
                    onClick={openFilterDialog}
                  />
                  <Button
                    type="button"
                    icon="pi pi-filter-slash"
                    rounded
                    text
                    severity="secondary"
                    aria-label="Clear calendar filters"
                    tooltip="Clear filters"
                    tooltipOptions={{ position: "top" }}
                    onClick={clearFilters}
                  />
                </div>
              </div>

              {loading ? (
                <div className="flex justify-content-center p-4">
                  <ProgressSpinner style={{ width: "48px", height: "48px" }} strokeWidth="4" />
                </div>
              ) : upcomingItems.length === 0 ? (
                <div
                  style={{
                    border: "1px dashed #cbd5e1",
                    borderRadius: "16px",
                    padding: "1.1rem 1.2rem",
                    background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
                    color: "#596F80",
                  }}
                >
                  {showCancelledUpcomingOnly
                    ? "No cancelled or deleted upcoming items for the current filters."
                    : "No upcoming items for the current filters."}
                </div>
              ) : (
                <>
                  <div className="hidden md:block">
                    <div
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "18px",
                        overflow: "hidden",
                        background: "#ffffff",
                        boxShadow: "0 14px 34px rgba(15, 23, 42, 0.05)",
                      }}
                    >
                      <DataTable
                        value={upcomingItems}
                        size="small"
                        responsiveLayout="scroll"
                        stripedRows
                        rowHover
                      >
                        <Column
                          header="Title"
                          body={(item) => (
                            <div style={{ minWidth: "12rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", ...(getCalendarItemTextStyle(item) || {}) }}>
                                {item.title || "Untitled event"}
                              </div>
                            </div>
                          )}
                        />
                        <Column
                          header="When"
                          body={(item) => (
                            <div style={{ minWidth: "10rem", color: "#596F80", ...(getCalendarItemTextStyle(item, "#596F80") || {}) }}>
                              {formatCalendarDateTime(item.startAt)}
                            </div>
                          )}
                        />
                        <Column
                          header={isClinicalStaffRole ? "Provider / Home / Resident" : "Home / Resident"}
                          body={(item) => renderUpcomingLinkage(item)}
                        />
                        <Column
                          header="Created By"
                          body={(item) => (
                            <div style={{ minWidth: "10rem" }}>
                              {renderCreatedByLabel(item)}
                            </div>
                          )}
                        />
                        <Column
                          header="Tags"
                          body={(item) => renderUpcomingTags(item)}
                        />
                        <Column
                          header="Actions"
                          body={(item) => renderUpcomingActions(item)}
                          style={{ width: "1%", whiteSpace: "nowrap" }}
                        />
                      </DataTable>
                    </div>
                  </div>
                  <div className="grid md:hidden">
                    {upcomingItems.map((item, index) => {
                      const appearance = getEventTypeAppearance(item.eventType || "meeting");
                      return (
                        <div key={getCalendarItemKey(item, index)} className="col-12">
                          <div
                            style={{
                              border: `1px solid ${appearance.accent}33`,
                              borderLeft: `5px solid ${appearance.accent}`,
                              borderRadius: "18px",
                              padding: "0.95rem 1rem",
                              background: appearance.background,
                              boxShadow: "0 12px 28px rgba(15, 23, 42, 0.05)",
                            }}
                          >
                            <div className="flex align-items-start justify-content-between gap-3">
                              <div>
                                <div style={{ fontWeight: 700, color: "#0f172a", ...(getCalendarItemTextStyle(item) || {}) }}>
                                  {item.title || "Untitled event"}
                                </div>
                                <div className="mt-1" style={{ color: "#596F80", ...(getCalendarItemTextStyle(item, "#596F80") || {}) }}>
                                  {formatCalendarDateTime(item.startAt)}
                                </div>
                              </div>
                              {renderUpcomingTags(item)}
                            </div>
                            <div className="mt-3">
                              {renderUpcomingLinkage(item)}
                            </div>
                            <div className="mt-2" style={{ fontSize: "0.92rem" }}>
                              <span style={{ color: "#94a3b8", fontWeight: 600 }}>Created by: </span>
                              {renderCreatedByLabel(item)}
                            </div>
                            <div className="mt-3">
                              {renderUpcomingActions(item)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </Card>
          </div>

          <div className="col-12">
            <Card style={sharedCalendarSurfaceCardStyle}>
              <div className="flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  <div style={sharedCalendarCardHeaderStyle}>Calendar</div>
                  <div className="mt-1" style={sharedCalendarCardSubtleTextStyle}>{activeCalendarLabel}</div>
                </div>
                <div className="flex align-items-center gap-2 flex-wrap">
                  <Dropdown
                    value={selectedCalendarView}
                    options={CALENDAR_VIEW_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(event) => setSelectedCalendarView(event.value)}
                    style={{ minWidth: "140px" }}
                  />
                  <Button
                    icon="pi pi-chevron-left"
                    text
                    rounded
                    onClick={() => shiftCalendarWindow(-1)}
                  />
                  <Button
                    label="Today"
                    text
                    onClick={() => setSelectedCalendarDate(new Date())}
                  />
                  <Button
                    icon="pi pi-chevron-right"
                    text
                    rounded
                    onClick={() => shiftCalendarWindow(1)}
                  />
                </div>
              </div>

              {loading ? (
                <div className="mt-4 flex justify-content-center">
                  <ProgressSpinner style={{ width: "48px", height: "48px" }} strokeWidth="4" />
                </div>
              ) : (
                <div
                  className="mt-4 p-3"
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "16px",
                    background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
                  }}
                >
                  <div className="flex align-items-center justify-content-between flex-wrap gap-2">
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{activeCalendarLabel}</div>
                    <Tag value={`${filteredEvents.length} item${filteredEvents.length === 1 ? "" : "s"}`} severity="info" />
                  </div>

                  {selectedCalendarView === "month" ? (
                    <div
                      className="mt-3"
                      style={{
                        display: "grid",
                        gridTemplateColumns: mobile ? "1fr" : "repeat(7, minmax(0, 1fr))",
                        gap: mobile ? "0.9rem" : "0.75rem",
                      }}
                    >
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label) => (
                        <div key={label} className="hidden md:block" style={{ fontWeight: 700, color: "#596F80", padding: "0 0.25rem" }}>{label}</div>
                      ))}
                      {monthGridDays.map((day) => {
                        const dayKey = getLosAngelesDayKey(day);
                        const dayEvents = eventsByDayKey[dayKey] || [];
                        const isCurrentMonth = day.getMonth() === selectedCalendarDate.getMonth();
                        const isToday = dayKey === getLosAngelesDayKey(new Date());
                        const isHoveredDay = hoveredCalendarDayKey === dayKey;

                        return (
                          <div
                            key={day.toISOString()}
                            onMouseEnter={() => setHoveredCalendarDayKey(dayKey)}
                            onMouseLeave={() => setHoveredCalendarDayKey((current) => (current === dayKey ? "" : current))}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={(event) => {
                              event.preventDefault();
                              const eventId = event.dataTransfer.getData("text/plain");
                              const draggedItem = filteredEvents.find((entry) => entry.eventId === eventId);
                              if (draggedItem) {
                                void moveEventToDay(draggedItem, dayKey);
                              }
                            }}
                            style={{
                              position: "relative",
                              minHeight: mobile ? "170px" : "140px",
                              border: `1px solid ${isToday ? "#f59e0b" : "#e2e8f0"}`,
                              borderRadius: "14px",
                              padding: mobile ? "0.9rem" : "0.75rem",
                              background: isCurrentMonth ? "#ffffff" : "#f8fafc",
                              opacity: isCurrentMonth ? 1 : 0.65,
                            }}
                          >
                            <div className="flex align-items-center justify-content-between gap-2">
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>{formatCalendarDayLabel(day, mobile ? { weekday: "long", month: "short", day: "numeric" } : { day: "numeric" })}</div>
                              <div className="flex align-items-center gap-2">
                                {dayEvents.length ? <Tag value={String(dayEvents.length)} severity="info" /> : null}
                                {dayEvents.length ? (
                                  <Button
                                    label="View All"
                                    link
                                    size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openDayEventsDialog(day, dayEvents);
                                    }}
                                    style={{
                                      opacity: isHoveredDay ? 1 : 0,
                                      pointerEvents: isHoveredDay ? "auto" : "none",
                                      transition: "opacity 120ms ease",
                                      padding: 0,
                                    }}
                                  />
                                ) : null}
                                <Button
                                  icon="pi pi-plus"
                                  rounded
                                  size="small"
                                  aria-label={`Create event for ${formatCalendarDayLabel(day, { month: "long", day: "numeric" })}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openCreateDialogForDay(day);
                                  }}
                                  style={getCalendarHoverCreateButtonStyle(isHoveredDay)}
                                />
                              </div>
                            </div>

                            <div className="mt-2 flex flex-column gap-2">
                              {dayEvents.slice(0, 3).map((item, index) => {
                                const appearance = getEventTypeAppearance(item.eventType || "meeting");
                                return (
                                  <Button
                                    key={getCalendarItemKey(item, index)}
                                    label={item.title || "Untitled event"}
                                    text
                                    size="small"
                                    className="justify-content-start"
                                    style={{
                                      padding: 0,
                                      color: isInactiveCalendarItem(item) ? "#b91c1c" : appearance.accent,
                                      ...(getCalendarItemTextStyle(item, isInactiveCalendarItem(item) ? "#b91c1c" : appearance.accent) || {}),
                                    }}
                                    draggable={canEditCalendarItem(item) && !isCancelledCalendarItem(item)}
                                    onDragStart={(event) => {
                                      event.dataTransfer.setData("text/plain", item.eventId);
                                      setDraggingEventId(item.eventId);
                                    }}
                                    onDragEnd={() => setDraggingEventId("")}
                                    onMouseEnter={(event) => openQuickPreview(item, event.currentTarget)}
                                    onMouseLeave={scheduleQuickPreviewClose}
                                    onClick={(event) => canEditCalendarItem(item) ? openEditDialog(item) : openQuickPreview(item, event.currentTarget)}
                                  />
                                );
                              })}
                              {dayEvents.length > 3 ? (
                                <div style={{ color: "#596F80", fontSize: "0.9rem" }}>
                                  +{dayEvents.length - 3} more
                                </div>
                              ) : null}
                            </div>
                            {dayEvents.length ? (
                              <div className="mt-2 flex md:hidden align-items-center justify-content-between gap-2">
                                <div style={{ color: "#596F80", fontSize: "0.9rem", fontWeight: 600 }}>
                                  {`${dayEvents.length} event${dayEvents.length === 1 ? "" : "s"}`}
                                </div>
                                <Button
                                  label="View All"
                                  link
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openDayEventsDialog(day, dayEvents);
                                  }}
                                  style={{ padding: 0 }}
                                />
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : selectedCalendarView === "week" ? (
                    <div
                      className="mt-3"
                      style={{
                        display: "grid",
                        gridTemplateColumns: mobile ? "1fr" : "repeat(7, minmax(0, 1fr))",
                        gap: mobile ? "0.9rem" : "0.75rem",
                      }}
                    >
                      {weekDays.map((day) => {
                        const dayKey = getLosAngelesDayKey(day);
                        const dayEvents = eventsByDayKey[dayKey] || [];
                        const isHoveredDay = hoveredCalendarDayKey === dayKey;
                        return (
                          <div
                            key={day.toISOString()}
                            onMouseEnter={() => setHoveredCalendarDayKey(dayKey)}
                            onMouseLeave={() => setHoveredCalendarDayKey((current) => (current === dayKey ? "" : current))}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={(event) => {
                              event.preventDefault();
                              const eventId = event.dataTransfer.getData("text/plain");
                              const draggedItem = filteredEvents.find((entry) => entry.eventId === eventId);
                              if (draggedItem) {
                                void moveEventToDay(draggedItem, dayKey);
                              }
                            }}
                            style={{
                              position: "relative",
                              minHeight: mobile ? "190px" : "220px",
                              border: draggingEventId ? "1px dashed #185da2" : "1px solid #e2e8f0",
                              borderRadius: "14px",
                              padding: mobile ? "0.95rem" : "0.85rem",
                              background: "#ffffff",
                            }}
                          >
                            <div className="flex align-items-center justify-content-between gap-2">
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>
                                {formatCalendarDayLabel(day, mobile ? { weekday: "long", month: "long", day: "numeric" } : { weekday: "short", month: "short", day: "numeric" })}
                              </div>
                              <div className="flex align-items-center gap-2">
                                {dayEvents.length ? (
                                  <Button
                                    label="View All"
                                    link
                                    size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openDayEventsDialog(day, dayEvents);
                                    }}
                                    style={{
                                      opacity: isHoveredDay ? 1 : 0,
                                      pointerEvents: isHoveredDay ? "auto" : "none",
                                      transition: "opacity 120ms ease",
                                      padding: 0,
                                    }}
                                  />
                                ) : null}
                                <Button
                                  icon="pi pi-plus"
                                  rounded
                                  size="small"
                                  aria-label={`Create event for ${formatCalendarDayLabel(day, { month: "long", day: "numeric" })}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openCreateDialogForDay(day);
                                  }}
                                  style={getCalendarHoverCreateButtonStyle(isHoveredDay)}
                                />
                              </div>
                            </div>
                            <div className="mt-3 flex flex-column gap-2">
                              {dayEvents.length === 0 ? (
                                <div style={{ color: "#94a3b8" }}>No events</div>
                              ) : dayEvents.map((item, index) => {
                                const appearance = getEventTypeAppearance(item.eventType || "meeting");
                                return (
                                  <div
                                    key={getCalendarItemKey(item, index)}
                                    draggable={!isCancelledCalendarItem(item)}
                                    onDragStart={(event) => {
                                      event.dataTransfer.setData("text/plain", item.eventId);
                                      setDraggingEventId(item.eventId);
                                    }}
                                    onDragEnd={() => setDraggingEventId("")}
                                    onMouseEnter={(event) => openQuickPreview(item, event.currentTarget)}
                                    onMouseLeave={scheduleQuickPreviewClose}
                                    style={{
                                      border: draggingEventId === item.eventId
                                        ? `1px solid ${isInactiveCalendarItem(item) ? "#dc2626" : appearance.accent}`
                                        : `1px solid ${appearance.accent}33`,
                                      borderLeft: `4px solid ${isInactiveCalendarItem(item) ? "#dc2626" : appearance.accent}`,
                                      borderRadius: "12px",
                                      padding: "0.65rem",
                                      background: appearance.background,
                                      cursor: !isCancelledCalendarItem(item) ? "grab" : "default",
                                    }}
                                  >
                                    <div style={{ fontWeight: 700, color: "#0f172a", ...(getCalendarItemTextStyle(item) || {}) }}>
                                      {item.title || "Untitled event"}
                                    </div>
                                    <div className="mt-1" style={{ color: "#596F80", fontSize: "0.92rem", ...(getCalendarItemTextStyle(item, "#596F80") || {}) }}>
                                      {formatCalendarDateTime(item.startAt)}
                                    </div>
                                    <div className="mt-2 flex gap-2">
                                      <Button
                                        label="View Details"
                                        size="small"
                                        rounded
                                        outlined
                                        severity="warning"
                                        icon="pi pi-eye"
                                        onClick={() => openCalendarDetails(item)}
                                      />
                                      <Button
                                        label="Edit"
                                        size="small"
                                        text
                                        onClick={() => canEditCalendarItem(item) && openEditDialog(item)}
                                      />
                                      <Button
                                        label="History"
                                        size="small"
                                        text
                                        onClick={() => void loadEventHistory(item)}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="mt-3 flex align-items-center justify-content-between gap-2 md:hidden">
                              <div style={{ color: dayEvents.length === 0 ? "#94a3b8" : "#596F80", fontSize: "0.9rem", fontWeight: 600 }}>
                                {dayEvents.length === 0
                                  ? "No events"
                                  : `${dayEvents.length} event${dayEvents.length === 1 ? "" : "s"}`}
                              </div>
                              {dayEvents.length ? (
                                <Button
                                  label="View All"
                                  link
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openDayEventsDialog(day, dayEvents);
                                  }}
                                  style={{ padding: 0 }}
                                />
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      className="mt-3"
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "16px",
                        background: "#ffffff",
                        padding: "1rem",
                      }}
                    >
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>
                        {formatCalendarDayLabel(selectedCalendarDate, { weekday: "long", month: "long", day: "numeric" })}
                      </div>
                      <div className="mt-3 flex flex-column gap-3">
                        {(eventsByDayKey[getLosAngelesDayKey(selectedCalendarDate)] || []).length === 0 ? (
                          <div style={{ color: "#94a3b8" }}>No events for this day</div>
                        ) : (eventsByDayKey[getLosAngelesDayKey(selectedCalendarDate)] || []).map((item, index) => {
                          const appearance = getEventTypeAppearance(item.eventType || "meeting");
                          return (
                            <div
                              key={getCalendarItemKey(item, index)}
                              draggable={!isCancelledCalendarItem(item)}
                              onDragStart={(event) => {
                                event.dataTransfer.setData("text/plain", item.eventId);
                                setDraggingEventId(item.eventId);
                              }}
                              onDragEnd={() => setDraggingEventId("")}
                              onMouseEnter={(event) => openQuickPreview(item, event.currentTarget)}
                              onMouseLeave={scheduleQuickPreviewClose}
                              style={{
                                border: draggingEventId === item.eventId
                                  ? `1px solid ${isInactiveCalendarItem(item) ? "#dc2626" : appearance.accent}`
                                  : `1px solid ${appearance.accent}33`,
                                borderLeft: `4px solid ${isInactiveCalendarItem(item) ? "#dc2626" : appearance.accent}`,
                                borderRadius: "14px",
                                padding: "0.9rem 1rem",
                                background: appearance.background,
                                cursor: !isCancelledCalendarItem(item) ? "grab" : "default",
                              }}
                            >
                              <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
                                <div>
                                  <div style={{ fontWeight: 700, color: "#0f172a", ...(getCalendarItemTextStyle(item) || {}) }}>
                                    {item.title || "Untitled event"}
                                  </div>
                                  <div className="mt-1" style={{ color: "#596F80", ...(getCalendarItemTextStyle(item, "#596F80") || {}) }}>
                                    {formatCalendarDateTime(item.startAt)}
                                  </div>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                  <Tag
                                    value={getEventTypeLabel(item.eventType || "meeting")}
                                    severity={appearance.tagSeverity}
                                  />
                                  <Tag value={formatCalendarStatusLabel(item)} severity={getCalendarItemStatus(item) === "scheduled" ? "success" : "danger"} />
                                </div>
                              </div>
                              <div className="mt-3 flex flex-wrap gap-2">
                                {item.homeName ? <Chip label={`Home: ${item.homeName}`} /> : null}
                                {item.residentName ? <Chip label={`Resident: ${item.residentName}`} /> : null}
                              </div>
                              <div className="mt-3 flex gap-2">
                                <Button label="View Details" size="small" rounded outlined severity="warning" icon="pi pi-eye" onClick={() => openCalendarDetails(item)} />
                                {canEditCalendarItem(item) ? <Button label="Edit" size="small" text onClick={() => openEditDialog(item)} /> : null}
                                <Button label="History" size="small" text onClick={() => void loadEventHistory(item)} />
                                {!isCancelledCalendarItem(item) ? (
                                  <Button
                                    label="Cancel"
                                    size="small"
                                    text
                                    severity="danger"
                                    onClick={() => requestCancelEvent(item)}
                                    disabled={saving}
                                  />
                                ) : null}
                                {canManageCalendarItem(item) ? (
                                  <Button
                                    label="Delete"
                                    size="small"
                                    text
                                    severity="danger"
                                    onClick={() => requestDeleteEvent(item)}
                                    disabled={saving}
                                  />
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      </div>
                    )}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      {quickPreviewVisible && quickPreviewItem ? (
        <div
          ref={quickPreviewPopupRef}
          onMouseEnter={clearQuickPreviewCloseTimeout}
          onMouseLeave={scheduleQuickPreviewClose}
          className="flex flex-column gap-3"
          style={{
            position: "fixed",
            top: quickPreviewPosition.top,
            left: quickPreviewPosition.left,
            width: "min(92vw, 360px)",
            zIndex: 1100,
            background: "linear-gradient(180deg, #fffdf8 0%, #ffffff 34%, #f8fbff 100%)",
            border: "1px solid #dbe7f1",
            borderRadius: "18px",
            boxShadow: "0 24px 50px rgba(15, 23, 42, 0.18)",
            padding: "0.95rem",
            maxHeight: "calc(100vh - 24px)",
            overflowY: "auto",
          }}
        >
          <div style={{ fontWeight: 700, color: "#0f172a" }}>Quick Details</div>
          <div style={{ color: "#64748b", fontSize: "0.9rem", marginTop: "-0.35rem" }}>
            Preview event information
          </div>

          <div
            style={{
              border: `1px solid ${getEventTypeAppearance(quickPreviewItem.eventType || "meeting").accent}33`,
              borderRadius: "16px",
              padding: "1rem",
              background: getEventTypeAppearance(quickPreviewItem.eventType || "meeting").background,
            }}
          >
            <div className="flex align-items-start justify-content-between gap-2 flex-wrap">
              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "1.05rem", ...(getCalendarItemTextStyle(quickPreviewItem) || {}) }}>
                {quickPreviewItem.title || "Untitled event"}
              </div>
              <Tag
                value={getEventTypeLabel(quickPreviewItem.eventType || "meeting")}
                severity={getEventTypeAppearance(quickPreviewItem.eventType || "meeting").tagSeverity}
              />
            </div>
            <div className="mt-2 flex gap-2 flex-wrap">
              <Tag
                value={formatCalendarStatusLabel(quickPreviewItem)}
                severity={getCalendarItemStatus(quickPreviewItem) === "scheduled" ? "success" : "danger"}
              />
              <Tag value={formatCalendarDateTime(quickPreviewItem.startAt)} severity="info" />
            </div>
          </div>

          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "16px",
              padding: "1rem",
              background: "#ffffff",
            }}
            className="flex flex-column gap-3"
          >
            <div>
              <div style={{ color: "#94a3b8", fontWeight: 600, fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                When
              </div>
              <div className="mt-1" style={{ color: "#0f172a", fontWeight: 600 }}>
                {formatCalendarDateTime(quickPreviewItem.startAt)}
              </div>
            </div>

            {quickPreviewItem.homeName ? (
              <div>
                <div style={{ color: "#94a3b8", fontWeight: 600, fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Home
                </div>
                <div className="mt-1" style={{ color: "#0f172a" }}>
                  {quickPreviewItem.homeName}
                </div>
              </div>
            ) : null}

            {quickPreviewItem.residentName ? (
              <div>
                <div style={{ color: "#94a3b8", fontWeight: 600, fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Resident
                </div>
                <div className="mt-1" style={{ color: "#0f172a" }}>
                  {quickPreviewItem.residentName}
                </div>
              </div>
            ) : null}

            <div>
              <div style={{ color: "#94a3b8", fontWeight: 600, fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Created By
              </div>
              <div className="mt-1" style={{ color: "#0f172a" }}>
                {quickPreviewItem.createdByName || "Unknown user"}
              </div>
            </div>

            {quickPreviewItem.description ? (
              <div>
                <div style={{ color: "#94a3b8", fontWeight: 600, fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Notes
                </div>
                <div className="mt-1" style={{ color: "#475569", lineHeight: 1.6 }}>
                  {quickPreviewItem.description}
                </div>
              </div>
            ) : null}

            <div className="flex gap-2 flex-wrap">
              <Button label="View Details" size="small" rounded severity="warning" icon="pi pi-eye" outlined onClick={() => {
                closeQuickPreview();
                openCalendarDetails(quickPreviewItem);
              }} />
              {canEditCalendarItem(quickPreviewItem) ? (
                <Button label="Open" size="small" onClick={() => {
                  closeQuickPreview();
                  openEditDialog(quickPreviewItem);
                }}
                />
              ) : null}
              <Button label="History" size="small" text onClick={() => {
                closeQuickPreview();
                void loadEventHistory(quickPreviewItem);
              }}
              />
            </div>
          </div>
        </div>
      ) : null}

      <Dialog
        visible={detailsDialogVisible}
        onHide={() => {
          setDetailsDialogVisible(false);
          setSelectedDetailsItem(null);
        }}
        header="Calendar Event Details"
        modal
        style={{ width: "min(94vw, 42rem)" }}
        footer={(
          <div className="flex justify-content-end">
            <Button
              type="button"
              label="Close"
              severity="secondary"
              outlined
              rounded
              onClick={() => {
                setDetailsDialogVisible(false);
                setSelectedDetailsItem(null);
              }}
            />
          </div>
        )}
      >
        {selectedDetailsItem ? (
          <div className="flex flex-column gap-3">
            <div style={{ ...sharedCalendarSurfaceCardStyle, padding: "1rem" }}>
              <div className="flex align-items-start justify-content-between gap-2 flex-wrap">
                <div style={{ fontWeight: 800, color: "#0f172a", fontSize: "1.15rem" }}>
                  {selectedDetailsItem.title || "Untitled event"}
                </div>
                <Tag
                  value={getEventTypeLabel(selectedDetailsItem.eventType || "meeting")}
                  severity={getEventTypeAppearance(selectedDetailsItem.eventType || "meeting").tagSeverity}
                />
              </div>
              <div className="mt-2 flex gap-2 flex-wrap">
                <Tag
                  value={formatCalendarStatusLabel(selectedDetailsItem)}
                  severity={getCalendarItemStatus(selectedDetailsItem) === "scheduled" ? "success" : "danger"}
                />
                {selectedDetailsItem.recurrenceRule?.frequency ? (
                  <Tag value={`Repeats ${selectedDetailsItem.recurrenceRule.frequency}`} severity="info" />
                ) : null}
              </div>
            </div>
            <div className="grid">
              <div className="col-12 md:col-6">
                <div style={{ ...sharedCalendarCardSubtleTextStyle, fontWeight: 700 }}>When</div>
                <div className="mt-1" style={{ color: "#0f172a" }}>{formatCalendarDateTime(selectedDetailsItem.startAt)}</div>
              </div>
              <div className="col-12 md:col-6">
                <div style={{ ...sharedCalendarCardSubtleTextStyle, fontWeight: 700 }}>Created by</div>
                <div className="mt-1" style={{ color: "#0f172a" }}>{selectedDetailsItem.createdByName || "Unknown user"}</div>
              </div>
              {selectedDetailsItem.homeName ? (
                <div className="col-12 md:col-6">
                  <div style={{ ...sharedCalendarCardSubtleTextStyle, fontWeight: 700 }}>Home</div>
                  <div className="mt-1" style={{ color: "#0f172a" }}>{selectedDetailsItem.homeName}</div>
                </div>
              ) : null}
              {selectedDetailsItem.residentName ? (
                <div className="col-12 md:col-6">
                  <div style={{ ...sharedCalendarCardSubtleTextStyle, fontWeight: 700 }}>Resident</div>
                  <div className="mt-1" style={{ color: "#0f172a" }}>{selectedDetailsItem.residentName}</div>
                </div>
              ) : null}
              {selectedDetailsItem.description ? (
                <div className="col-12">
                  <div style={{ ...sharedCalendarCardSubtleTextStyle, fontWeight: 700 }}>Notes</div>
                  <div className="mt-1" style={{ color: "#475569", whiteSpace: "pre-wrap" }}>{selectedDetailsItem.description}</div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </Dialog>

      <Dialog
        visible={filterDialogVisible}
        onHide={() => setFilterDialogVisible(false)}
        header="Calendar Filters"
        modal
        style={{ width: "min(94vw, 34rem)" }}
        footer={(
          <div className="flex justify-content-end gap-2">
            <Button type="button" label="Reset" icon="pi pi-refresh" severity="secondary" text onClick={resetFilterDraft} />
            <Button type="button" label="Cancel" severity="secondary" outlined rounded onClick={() => setFilterDialogVisible(false)} />
            <Button type="button" label="Apply" icon="pi pi-check" severity="warning" rounded onClick={applyFilters} />
          </div>
        )}
      >
        <div className="flex flex-column gap-3">
          <div>
            <label htmlFor="shared-calendar-dialog-scope" className="mb-2 block">{filterGroupLabel} / Residents</label>
            <Dropdown
              id="shared-calendar-dialog-scope"
              value={draftScopeValue}
              options={groupedScopeOptions}
              optionGroupLabel="label"
              optionGroupChildren="items"
              optionGroupTemplate={scopeOptionGroupTemplate}
              optionLabel="label"
              optionValue="value"
              itemTemplate={scopeItemTemplate}
              onChange={(event) => setDraftScopeValue(String(event.value || "all"))}
              style={{ width: "100%" }}
              placeholder={`All ${filterGroupLabel.toLowerCase()} and residents`}
            />
          </div>
          <div>
            <label htmlFor="shared-calendar-dialog-event-type" className="mb-2 block">Event Type</label>
            <Dropdown
              id="shared-calendar-dialog-event-type"
              value={draftEventType}
              options={eventTypeOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(event) => setDraftEventType(String(event.value || "all"))}
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label htmlFor="shared-calendar-dialog-search" className="mb-2 block">Search</label>
            <InputText
              id="shared-calendar-dialog-search"
              value={draftSearchValue}
              onChange={(event) => setDraftSearchValue(event.target.value)}
              placeholder="Home, acronym, resident, title..."
              style={{ width: "100%" }}
            />
          </div>
          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="shared-calendar-dialog-active-only"
              checked={draftActiveOnly}
              onChange={(event) => setDraftActiveOnly(Boolean(event.checked))}
            />
            <label htmlFor="shared-calendar-dialog-active-only">Active Only</label>
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={eventTypeDialogVisible}
        onHide={() => setEventTypeDialogVisible(false)}
        header="Manage Calendar Event Types"
        modal
        style={{ width: "min(94vw, 42rem)" }}
        footer={editingEventTypeId ? (
          <div className="flex justify-content-end gap-2">
            <Button type="button" label="Cancel" severity="secondary" outlined onClick={() => setEventTypeDialogVisible(false)} disabled={eventTypeSaving} />
            <Button type="button" label="Save Changes" icon="pi pi-check" onClick={() => void saveEventType()} loading={eventTypeSaving} />
          </div>
        ) : null}
      >
        <div className="flex flex-column gap-3">
          <div className="p-3" style={{ border: "1px solid #dbeafe", borderRadius: "12px", background: "#f8fbff" }}>
            <div style={{ fontWeight: 700 }}>Custom event types</div>
            <div className="mt-1" style={sharedCalendarCardSubtleTextStyle}>
              Add event types that your team uses. Existing events keep their type if a custom type is archived.
            </div>
          </div>
          <div>
              <label htmlFor="shared-calendar-event-type-name" className="mb-2 block">Event Type Name</label>
              <div className="flex align-items-center gap-2">
              <InputText
                  id="shared-calendar-event-type-name"
                  value={eventTypeName}
                  maxLength={30}
                  onChange={(event) => setEventTypeName(event.target.value)}
                  placeholder="For example, Staff Meeting"
                  style={{ flex: 1 }}
                />
                <Button
                  type="button"
                  label="Add New Event Type"
                  icon="pi pi-plus"
                  severity="warning"
                  outlined
                  rounded
                  onClick={() => void saveEventType(true)}
                  loading={eventTypeSaving}
                />
              </div>
              <small className="mt-1 block" style={{ color: "#64748b" }}>Maximum 30 characters.</small>
          </div>
          <div className="flex flex-column gap-2">
            {eventTypeOptions.filter((option) => option.value !== "all").map((option) => (
              <div key={option.value} className="flex align-items-center justify-content-between gap-2 p-2" style={{ border: "1px solid #e2e8f0", borderRadius: "10px" }}>
                <div className="flex align-items-center gap-2">
                  <span style={{ width: "0.8rem", height: "0.8rem", borderRadius: "50%", background: option.color || "#2563eb" }} />
                  <span>{option.label}</span>
                  {option.isSystemDefault ? <Tag value="Default" severity="secondary" /> : option.isActive === false ? <Tag value="Archived" severity="danger" /> : null}
                </div>
                {!option.isSystemDefault && option.isActive !== false ? (
                  <div className="flex align-items-center gap-1">
                    <Button type="button" icon="pi pi-pencil" text rounded aria-label={`Edit ${option.label}`} onClick={() => openEditEventTypeDialog(option)} />
                    <Button type="button" icon="pi pi-archive" text rounded severity="danger" aria-label={`Archive ${option.label}`} onClick={() => requestArchiveEventType(option)} />
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={Boolean(pendingArchiveEventType)}
        onHide={() => setPendingArchiveEventType(null)}
        header="Archive Event Type"
        modal
        style={{ width: "min(94vw, 28rem)" }}
        footer={(
          <div className="flex justify-content-end gap-2">
            <Button type="button" label="Keep" severity="secondary" outlined onClick={() => setPendingArchiveEventType(null)} disabled={eventTypeSaving} />
            <Button type="button" label="Archive" icon="pi pi-archive" severity="danger" onClick={() => void archiveEventType()} loading={eventTypeSaving} />
          </div>
        )}
      >
        <p className="m-0">Archive <strong>{pendingArchiveEventType?.label}</strong>? Existing events will remain unchanged, but this type will no longer be available for new events.</p>
      </Dialog>

      <Dialog
        visible={eventDialogVisible}
        onHide={closeEventDialog}
        header={eventDialogMode === "edit" ? "Edit Shared Calendar Event" : "Create Shared Calendar Event"}
        style={{ width: "95vw", maxWidth: "900px" }}
        contentStyle={{
          background: "linear-gradient(180deg, #fffdf7 0%, #ffffff 28%, #f8fbff 100%)",
          borderRadius: "0 0 18px 18px",
        }}
        headerStyle={{
          background: "linear-gradient(135deg, #6c6c6c 0%, #f5d361 100%)",
          color: "#ffffff",
          borderRadius: "18px 18px 0 0",
        }}
        footer={eventDialogFooter}
      >
        <div className="grid">
          <div className="col-12">
            <label htmlFor="shared-calendar-form-title" className="mb-2 block">Title</label>
            <InputText
              id="shared-calendar-form-title"
              value={eventForm.title}
              onChange={(event) => updateEventFormField("title", event.target.value)}
              style={{ width: "100%" }}
            />
          </div>
          {eventDialogMode === "edit" ? (
            <div className="col-12 md:col-6">
              <label htmlFor="shared-calendar-form-created-by" className="mb-2 block">Created By</label>
              <InputText
                id="shared-calendar-form-created-by"
                value={editingEventContext?.createdByName || "Unknown user"}
                readOnly
                style={{ width: "100%" }}
              />
            </div>
          ) : null}
          {eventDialogMode === "edit" ? (
            <div className="col-12 md:col-6">
              <label htmlFor="shared-calendar-form-status" className="mb-2 block">Status</label>
              <InputText
                id="shared-calendar-form-status"
                value={formatCalendarStatusLabel(editingEventContext)}
                readOnly
                style={{
                  width: "100%",
                  color: isInactiveCalendarItem(editingEventContext) ? "#b91c1c" : undefined,
                  fontWeight: isInactiveCalendarItem(editingEventContext) ? 700 : undefined,
                }}
              />
            </div>
          ) : null}
          <div className="col-12">
            <div
              style={{
                border: "1px solid #dbeafe",
                borderRadius: "18px",
                padding: "1rem",
                background: "linear-gradient(135deg, rgba(239, 246, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 45%, rgba(255, 247, 237, 0.94) 100%)",
                boxShadow: "0 16px 38px rgba(15, 23, 42, 0.06)",
              }}
            >
              <div className="grid">
                <div className="col-12 md:col-5">
                  <label htmlFor="shared-calendar-form-type" className="mb-2 block">Event Type</label>
                  <Dropdown
                    id="shared-calendar-form-type"
                    value={eventForm.eventType}
                    options={eventTypeOptions.filter((option) => option.value !== "all" && option.isActive !== false)}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(event) => updateEventFormField("eventType", event.value)}
                    style={{ width: "100%" }}
                  />
                  {(canManageEventTypes || isAdminUser || userRoleType === ROLE_TYPE_PROVIDER) ? (
                    <Chip
                      label="Manage Event Types"
                      icon="pi pi-tags"
                      className="mt-2"
                      style={{
                        width: "fit-content",
                        cursor: "pointer",
                        background: "#fff3cd",
                        color: "#8a5a00",
                        border: "1px solid #f5c451",
                      }}
                      onClick={openCreateEventTypeDialog}
                    />
                  ) : null}
                </div>
                <div className="col-12 md:col-7">
                  <label htmlFor="shared-calendar-form-scope-picker" className="mb-2 block">{eventFormScopeLabel}</label>
                  <Dropdown
                    id="shared-calendar-form-scope-picker"
                    value={selectedEventScopeValue}
                    options={eventFormScopeOptions}
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    optionGroupTemplate={eventFormScopeOptionGroupTemplate}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(event) => applyEventFormScopeValue(event.value)}
                    itemTemplate={eventFormScopeOptionTemplate}
                    valueTemplate={eventFormScopeValueTemplate}
                    style={{ width: "100%" }}
                    placeholder={eventFormScopePlaceholder}
                    disabled={!hasHomeOptions}
                    showClear
                  />
                </div>
              </div>
            </div>
          </div>
          {eventDialogMode === "edit" && editingEventContext?.occurrenceId && editingEventContext?.recurrenceRule ? (
            <div className="col-12">
              <label htmlFor="shared-calendar-form-scope" className="mb-2 block">Apply Changes To</label>
              <Dropdown
                id="shared-calendar-form-scope"
                value={editScope}
                options={RECURRING_EDIT_SCOPE_OPTIONS}
                optionLabel="label"
                optionValue="value"
                onChange={(event) => setEditScope(event.value)}
                style={{ width: "100%" }}
              />
            </div>
          ) : null}
          <div className="col-12">
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "18px",
                padding: "1rem",
                background: "rgba(255, 255, 255, 0.92)",
              }}
            >
              <div className="grid">
                <div className="col-12 lg:col-6">
                  <label htmlFor="shared-calendar-form-description" className="mb-2 block">Description</label>
                  <InputTextarea
                    id="shared-calendar-form-description"
                    value={eventForm.description}
                    onChange={(event) => updateEventFormField("description", event.target.value)}
                    rows={4}
                    style={{ width: "100%" }}
                  />
                </div>
                <div className="col-12 lg:col-6">
                  <div className="grid">
                    <div className={shouldShowStartTimePicker ? "col-12 md:col-7" : "col-12"}>
                      <label htmlFor="shared-calendar-form-start" className="mb-2 block">{startDateLabel}</label>
                      <Calendar
                        id="shared-calendar-form-start"
                        value={normalizedStartAt}
                        onChange={(event) => updateStartDateValue(event.value)}
                        showIcon
                        minDate={startDateMinDate}
                        style={{ width: "100%" }}
                      />
                    </div>
                    {shouldShowStartTimePicker ? (
                      <div className="col-12 md:col-5">
                        <label htmlFor="shared-calendar-form-start-time" className="mb-2 block">{startTimeLabel}</label>
                        <Dropdown
                          id="shared-calendar-form-start-time"
                          value={selectedStartTimeValue}
                          options={availableStartTimeOptions}
                          optionLabel="label"
                          optionValue="value"
                          onChange={(event) => updateStartTimeValue(event.value)}
                          style={{ width: "100%" }}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="shared-calendar-form-reminder" className="mb-2 block">Email Reminder</label>
            <Dropdown
              id="shared-calendar-form-reminder"
              value={eventForm.notificationEmailReminder}
              options={reminderOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(event) => updateEventFormField("notificationEmailReminder", event.value)}
              style={{ width: "100%" }}
            />
            {eventForm.notificationEmailReminder !== "none" ? (
              <small
                style={{
                  display: "block",
                  marginTop: "0.5rem",
                  color: "#6b7280",
                  lineHeight: 1.4,
                }}
              >
                {eventForm.residentId
                  ? "Event owner and staff members who have access to the resident will receive email."
                  : "Event owner will receive an email."}
              </small>
            ) : null}
          </div>
          {!shouldHideRepeatSection ? (
            <>
              <div className="col-12 md:col-4">
                <label htmlFor="shared-calendar-form-repeat-enabled" className="mb-2 block">Repeat</label>
                <Dropdown
                  id="shared-calendar-form-repeat-enabled"
                  value={eventForm.repeatEnabled}
                  options={REPEAT_TOGGLE_OPTIONS}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(event) => updateEventFormField("repeatEnabled", event.value)}
                  style={{ width: "100%" }}
                />
              </div>
              {eventForm.repeatEnabled === "yes" ? (
                <div className="col-12 md:col-12">
                  <label className="mb-2 block">Repeats Every</label>
                  <div className="grid">
                    <div className="col-5 md:col-5">
                      <InputText
                        id="shared-calendar-form-recurrence-interval"
                        value={eventForm.recurrenceInterval}
                        onChange={(event) => updateEventFormField("recurrenceInterval", event.target.value)}
                        style={{ width: "100%" }}
                        inputMode="numeric"
                        type="number"
                        min={1}
                      />
                    </div>
                    <div className="col-4 md:col-4">
                      <Dropdown
                        id="shared-calendar-form-recurrence-frequency"
                        value={eventForm.recurrenceFrequency === "none" ? "weekly" : eventForm.recurrenceFrequency}
                        options={RECURRENCE_FREQUENCY_OPTIONS}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(event) => updateEventFormField("recurrenceFrequency", event.value)}
                        style={{ width: "100%" }}
                      />
                    </div>
                    <div className="col-4 md:col-4">
                      <label htmlFor="shared-calendar-form-recurrence-until" className="mb-2 block">Repeat Until</label>
                      <Calendar
                        id="shared-calendar-form-recurrence-until"
                        value={eventForm.recurrenceUntil}
                        onChange={(event) => updateEventFormField("recurrenceUntil", event.value)}
                        minDate={repeatUntilMinDate}
                        style={{ width: "100%", maxWidth: "22rem" }}
                      />
                    </div>
                  </div>
                </div>
              ) : null}

            </>
          ) : null}
        </div>
      </Dialog>

      <Dialog
        visible={cancelConfirmVisible}
        onHide={closeCancelConfirmDialog}
        header="Cancel Event"
        style={{ width: "92vw", maxWidth: "440px" }}
        footer={cancelConfirmDialogFooter}
        closable={!saving}
      >
        <div style={{ color: "#475569", lineHeight: 1.7 }}>
          {`Are you sure you want to cancel "${pendingCancelItem?.title || "this event"}"?`}
        </div>
      </Dialog>

      <Dialog
        visible={deleteConfirmVisible}
        onHide={closeDeleteConfirmDialog}
        header="Delete Event"
        style={{ width: "92vw", maxWidth: "440px" }}
        footer={deleteConfirmDialogFooter}
        closable={!saving}
      >
        <div style={{ color: "#475569", lineHeight: 1.7 }}>
          {`Are you sure you want to permanently delete "${pendingDeleteItem?.title || "this event"}"?`}
        </div>
      </Dialog>

      <Dialog
        visible={historyDialogVisible}
        onHide={() => setHistoryDialogVisible(false)}
        header={selectedHistoryEvent?.title ? `History: ${selectedHistoryEvent.title}` : "Event History"}
        style={{ width: "95vw", maxWidth: "760px" }}
        footer={historyDialogFooter}
      >
        {historyLoading ? (
          <div className="flex justify-content-center p-4">
            <ProgressSpinner style={{ width: "42px", height: "42px" }} strokeWidth="4" />
          </div>
        ) : eventHistoryItems.length === 0 ? (
          <div
            style={{
              border: "1px dashed #cbd5e1",
              borderRadius: "16px",
              padding: "1.5rem",
              color: "#596F80",
            }}
          >
            No history entries yet for this shared calendar item.
          </div>
        ) : (
          <div className="flex flex-column gap-3">
            {eventHistoryItems.map((item) => (
              <div
                key={item.historyId || `${item.eventId}-${item.createdAt}`}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "14px",
                  padding: "1rem",
                  background: "#ffffff",
                }}
              >
                <div className="flex align-items-center justify-content-between gap-3 flex-wrap">
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>
                    {String(item.action || "updated").replace(/_/g, " ")}
                  </div>
                  <Tag value={formatCalendarDateTime(item.createdAt)} severity="info" />
                </div>
                <div className="mt-2" style={{ color: "#475569" }}>
                  By {item.createdByName || "Unknown user"}
                </div>
                {item.metadata && Object.keys(item.metadata).length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {Object.entries(item.metadata).map(([key, value]) => (
                      <Chip
                        key={key}
                        label={`${formatHistoryMetadataLabel(key)}: ${formatHistoryMetadataValue(key, value)}`}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Dialog>

      <Dialog
        visible={dayEventsDialogVisible}
        onHide={() => setDayEventsDialogVisible(false)}
        header={selectedDayEventsDate ? formatCalendarDayLabel(selectedDayEventsDate, { weekday: "long", month: "long", day: "numeric" }) : "Day Events"}
        style={{ width: "95vw", maxWidth: "980px" }}
        footer={dayEventsDialogFooter}
      >
        {selectedDayEvents.length === 0 ? (
          <div
            style={{
              border: "1px dashed #cbd5e1",
              borderRadius: "16px",
              padding: "1.25rem",
              background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
              color: "#596F80",
            }}
          >
            No events for this day.
          </div>
        ) : (
          <DataTable
            value={selectedDayEvents}
            size="small"
            responsiveLayout="scroll"
            stripedRows
            rowHover
          >
            <Column
              header="Title"
              body={(item) => (
                <div style={{ minWidth: "12rem" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a", ...(getCalendarItemTextStyle(item) || {}) }}>
                    {item.title || "Untitled event"}
                  </div>
                </div>
              )}
            />
            <Column
              header="When"
              body={(item) => (
                <div style={{ minWidth: "10rem", color: "#596F80", ...(getCalendarItemTextStyle(item, "#596F80") || {}) }}>
                  {formatCalendarDateTime(item.startAt)}
                </div>
              )}
            />
            <Column
              header={isClinicalStaffRole ? "Provider / Home / Resident" : "Home / Resident"}
              body={(item) => renderUpcomingLinkage(item)}
            />
            <Column
              header="Tags"
              body={(item) => renderUpcomingTags(item)}
            />
            <Column
              header=""
              body={(item) => renderDayEventsActions(item)}
              bodyStyle={{ width: "1%" }}
            />
          </DataTable>
        )}
      </Dialog>
      {isAdminUser ? <AdminResetControl label="Reset Calendar" description="Delete current and future one-time events and recurring series from the shared calendar. Past events are preserved." onReset={resetCalendar} /> : null}
    </div>
  );
};

export default SharedCalendarPage;
